import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  FiSend,
  FiX,
  FiTrash2,
  FiExternalLink,
  FiFileText,
  FiGrid,
  FiCheckCircle,
  FiLoader,
  FiMaximize2,
  FiMinimize2,
  FiPaperclip,
  FiClock,
  FiPlusCircle,
  FiTrendingUp,
  FiBarChart2,
  FiPackage,
  FiMapPin,
  FiMail,
  FiKey,
  FiCpu,
} from 'react-icons/fi'
import { BsStars } from 'react-icons/bs'
import { toast } from 'react-toastify'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import {
  XLSX,
  buildCorporateSheet,
  cellMoney,
  cellNumber,
  cellStatusBadge,
  cellText,
} from '../../utils/excelCorporateStyle'

import gisaAvatar from '../../assets/gid-ia-icon.svg'
import api from '../../services/axios'
import { useAuth } from '../../context/AuthContext'
import { InteractiveChartCard, GisaChartPayload } from './InteractiveChartCard'
import { CommandConfigModal, GisaCommandActionType } from './CommandConfigModal'
import {
  FloatingWidgetWrapper,
  FloatingBubbleButton,
  BubbleAvatarImg,
  OnlineDotBadge,
  DragIndicatorGrip,
  GripDot,
  FloatingTooltip,
  DrawerBackdrop,
  DrawerContainer,
  DrawerHeader,
  HeaderProfile,
  HeaderAvatarWrapper,
  HeaderAvatarImg,
  HeaderTitleGroup,
  HeaderNameRow,
  AssistantName,
  StatusPill,
  AssistantSubtitle,
  HeaderActions,
  HeaderIconButton,
  ContextBanner,
  ContextBadge,
  ContextModelTag,
  TasksActionBar,
  TaskActionBtn,
  TaskProgressCard,
  TaskProgressHeader,
  TaskProgressTitleRow,
  TaskProgressPctBadge,
  TaskProgressBarTrack,
  TaskProgressBarFill,
  TaskProgressStepDetail,
  ChatMessagesScroll,
  WelcomeDashboard,
  WelcomeHero,
  CapabilitiesGrid,
  CapabilityCard,
  CapabilityIconBox,
  CapabilityContent,
  MessageBubbleRow,
  MiniAvatarImg,
  MessageCard,
  MessageActionsToolbar,
  MessageExportButton,
  ActionButtonInChat,
  FollowUpsWrapper,
  FollowUpChip,
  TypingIndicator,
  TypingDot,
  DrawerFooter,
  ChatInputForm,
  SlashCommandButton,
  SlashPaletteMenu,
  SlashPaletteItem,
  SlashPaletteItemIcon,
  SlashPaletteItemInfo,
  SlashPaletteItemCommand,
  SlashPaletteItemDesc,
  ChatInputField,
  SendMessageButton,
} from './styles'

export interface ChatMessage {
  id: string
  sender: 'gisa' | 'user'
  text: string
  chartData?: GisaChartPayload
  reportData?: GisaChartPayload
  actionButton?: {
    label: string
    route: string
    pageId?: number
  }
  followUps?: string[]
  time: string
}

interface SavedConversation {
  id: string
  title: string
  createdAt: string
  messages: ChatMessage[]
}

interface SlashCommandOption {
  command: string
  label: string
  desc: string
  action: Exclude<GisaCommandActionType, null>
  color: string
  icon: React.ReactNode
}

const SLASH_COMMANDS_LIST: SlashCommandOption[] = [
  {
    command: '/sellout',
    label: 'Sellout & Faturamento',
    desc: 'Análise de faturamento (R$), volume e gráfico por distribuidor ou mês',
    action: 'sellout',
    color: '#0284c7',
    icon: <FiTrendingUp size={15} />,
  },
  {
    command: '/regularidade',
    label: 'Auditoria de Envios',
    desc: 'Verificar distribuidores em dia vs pendentes de envio (> 7 dias)',
    action: 'regularidade',
    color: '#8b5cf6',
    icon: <FiCheckCircle size={15} />,
  },
  {
    command: '/estoque',
    label: 'Estoque & Giro',
    desc: 'Diagnóstico da posição de estoque por distribuidor e SKUs',
    action: 'estoque',
    color: '#f97316',
    icon: <FiPackage size={15} />,
  },
  {
    command: '/pdvs',
    label: 'Cobertura & Giro de PDVs',
    desc: 'Analisar carteira de clientes ativos, giro de PDVs e potencial IBGE',
    action: 'pdvs',
    color: '#10b981',
    icon: <FiMapPin size={15} />,
  },
  {
    command: '/pdf',
    label: 'Gerar Relatório PDF',
    desc: 'Compilar e baixar Relatório Executivo do GID em PDF na hora',
    action: 'pdf',
    color: '#ef4444',
    icon: <FiFileText size={15} />,
  },
  {
    command: '/excel',
    label: 'Planilha Excel (.xlsx)',
    desc: 'Exportar pasta de trabalho multi-abas com indicadores do GID',
    action: 'excel',
    color: '#16a34a',
    icon: <FiGrid size={15} />,
  },
  {
    command: '/email',
    label: 'Disparar por E-mail',
    desc: 'Enviar boletim executivo do GID por e-mail imediatamente',
    action: 'email',
    color: '#eab308',
    icon: <FiMail size={15} />,
  },
]

const PAGE_NAMES: Record<number, string> = {
  0: 'Análise Geral & Mapa de Mercado',
  1: 'Regularidade de Envios',
  2: 'Análise de Estoque & Giro',
  3: 'Faseamento Comercial',
  4: 'Ranking de Cobertura & IBGE',
  5: 'Acompanhamento de Giro de PDVs',
  8: 'Central de Relatórios de Vendas',
  9: 'Controle de Usuários',
  10: 'Base de Dados & Arquivos Pendentes',
  11: 'Validação de Sellout',
  12: 'Monitor de Logs de Arquivos',
  13: 'Média de SKU & Matriz BCG',
  14: 'Integrações, API Keys & GID IA',
}

export function GisaAssistant() {
  const { user, signed } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const [isOpen, setIsOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [inputQuery, setInputQuery] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [isExecutingTask, setIsExecutingTask] = useState(false)
  const [taskStatusMsg, setTaskStatusMsg] = useState('')
  const [taskProgressPct, setTaskProgressPct] = useState(0)
  const [taskStepDetail, setTaskStepDetail] = useState('')
  const [currentPageId, setCurrentPageId] = useState<number>(0)

  // Status da Chave de IA configurada pelo usuário
  const [aiStatus, setAiStatus] = useState<{
    gisaUnlocked: boolean
    provider: string
    model: string
    keyName: string
  }>({
    gisaUnlocked: false,
    provider: 'gemini',
    model: 'gemini-2.5-flash',
    keyName: '',
  })

  // Slash commands & Modals
  const [showSlashPalette, setShowSlashPalette] = useState(false)
  const [selectedCommandAction, setSelectedCommandAction] = useState<GisaCommandActionType>(null)
  const [isCommandModalOpen, setIsCommandModalOpen] = useState(false)
  const [personalShortcuts, setPersonalShortcuts] = useState<
    Array<{
      id: number
      command: string
      label: string
      promptTemplate: string
      defaultAction: string
      defaultPeriod: string
      pinToBar: boolean
    }>
  >([])

  // Histórico local de conversas
  const [conversationsList, setConversationsList] = useState<SavedConversation[]>(() => {
    try {
      const raw = localStorage.getItem('@GID:gisa_conversations')
      return raw ? JSON.parse(raw) : []
    } catch {
      return []
    }
  })
  const [activeConversationId, setActiveConversationId] = useState<string>(() => {
    return localStorage.getItem('@GID:gisa_active_conv') || `conv_${Date.now()}`
  })
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false)

  const [messages, setMessages] = useState<ChatMessage[]>([])

  // Drag vertical da bolha flutuante
  const [topPosition, setTopPosition] = useState<number>(() => {
    const saved = localStorage.getItem('@GID:gisa_top_pos')
    if (saved) {
      const parsed = parseInt(saved, 10)
      if (!isNaN(parsed) && parsed >= 60 && parsed <= window.innerHeight - 90) {
        return parsed
      }
    }
    return Math.max(120, Math.min(window.innerHeight - 110, Math.round(window.innerHeight * 0.78)))
  })
  const isDraggingRef = useRef(false)
  const dragMovedRef = useRef(false)
  const dragStartYRef = useRef(0)
  const dragStartTopRef = useRef(0)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const userRole = String(user?.role || '').toLowerCase()
  const isAllowedRole =
    signed && ['administrador', 'admin', 'suporte', 'conferente'].includes(userRole)

  const checkAiConfigStatus = useCallback(async () => {
    if (!isAllowedRole) return
    try {
      const res = await api.get('/integracoes/ia-config')
      const data = res.data || {}
      const unlocked = Boolean(data.gisaUnlocked)
      setAiStatus({
        gisaUnlocked: unlocked,
        provider: data.provider || 'gemini',
        model: data.model || 'gemini-2.5-flash',
        keyName: data.name || 'Chave IA Ativa',
      })
      if (unlocked) {
        setMessages((prev) =>
          prev.filter((m) => !m.text.includes('🔒 **Chave de IA Necessária:**'))
        )
      }
    } catch {
      // Silencioso caso ainda não tenha chave
    }
  }, [isAllowedRole])

  const loadPersonalShortcuts = useCallback(async () => {
    if (!isAllowedRole) return
    try {
      const res = await api.get('/integracoes/gisa/atalhos')
      if (Array.isArray(res.data?.shortcuts)) {
        setPersonalShortcuts(res.data.shortcuts)
      }
    } catch {
      // Silencioso
    }
  }, [isAllowedRole])

  useEffect(() => {
    checkAiConfigStatus()
    loadPersonalShortcuts()
  }, [checkAiConfigStatus, loadPersonalShortcuts, isOpen])

  // Escuta eventos globais para abrir a GISA ou atualizar o status da chave de IA
  useEffect(() => {
    const handleOpenGisa = (ev: any) => {
      setIsOpen(true)
      checkAiConfigStatus()
      loadPersonalShortcuts()
      if (ev?.detail?.initialQuery) {
        setTimeout(() => {
          handleSendMessage(ev.detail.initialQuery)
        }, 250)
      }
    }
    const handleAiUpdated = () => {
      checkAiConfigStatus()
    }
    const handleShortcutsUpdated = () => {
      loadPersonalShortcuts()
    }
    const handlePageChanged = (ev: any) => {
      if (typeof ev?.detail?.pageId === 'number') {
        setCurrentPageId(ev.detail.pageId)
      }
    }

    window.addEventListener('gid:open-gisa', handleOpenGisa as EventListener)
    window.addEventListener('gid:ai-config-updated', handleAiUpdated)
    window.addEventListener('gid:gisa-shortcuts-updated', handleShortcutsUpdated)
    window.addEventListener('gid:page-changed', handlePageChanged as EventListener)
    return () => {
      window.removeEventListener('gid:open-gisa', handleOpenGisa as EventListener)
      window.removeEventListener('gid:ai-config-updated', handleAiUpdated)
      window.removeEventListener('gid:gisa-shortcuts-updated', handleShortcutsUpdated)
      window.removeEventListener('gid:page-changed', handlePageChanged as EventListener)
    }
  }, [checkAiConfigStatus, loadPersonalShortcuts])

  // Mensagem inicial de boas-vindas
  useEffect(() => {
    if (messages.length === 0) {
      const welcomeText = aiStatus.gisaUnlocked
        ? `Olá, **${user?.name || user?.nickname || 'Gestor'}**! Eu sou a **GID IA**, sua assistente de Inteligência Artificial do **GID**.\n\nEstou conectada ao modelo **${aiStatus.model}** (${aiStatus.provider.toUpperCase()}) e pronta para gerar análises de **Sellout**, auditar **Regularidade de Envios**, fazer **Levantamento de Distribuidores**, diagnosticar **Estoque & PDVs** ou exportar relatórios em **PDF** e **Excel**!`
        : `Olá, **${user?.name || user?.nickname || 'Gestor'}**! Eu sou a **GID IA**, assistente inteligente do **GID**.\n\nVocê já pode usar meus atalhos rápidos para gerar **PDF Executivo** e **Planilha Excel**, ou vincular sua **Chave de API de IA** na Central GID IA para liberar análises conversacionais completas!`

      setMessages([
        {
          id: 'welcome',
          sender: 'gisa',
          text: welcomeText,
          actionButton: !aiStatus.gisaUnlocked
            ? {
                label: 'Vincular Chave de IA na Central',
                route: '/painel',
                pageId: 14,
              }
            : undefined,
          followUps: [],
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ])
    }
  }, [aiStatus.gisaUnlocked, aiStatus.model, aiStatus.provider, user?.name, user?.nickname, messages.length])

  // Auto-scroll
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, isTyping, isOpen])

  // Salva conversa atual no histórico local
  useEffect(() => {
    if (messages.length <= 1) return
    const firstUserMsg = messages.find((m) => m.sender === 'user')?.text || 'Análise GID'
    const updatedConv: SavedConversation = {
      id: activeConversationId,
      title: firstUserMsg.slice(0, 42),
      createdAt: new Date().toISOString(),
      messages: messages.slice(-20),
    }
    setConversationsList((prev) => {
      const filtered = prev.filter((c) => c.id !== activeConversationId)
      const next = [updatedConv, ...filtered].slice(0, 12)
      try {
        localStorage.setItem('@GID:gisa_conversations', JSON.stringify(next))
      } catch {}
      return next
    })
  }, [messages, activeConversationId])

  // Escuta eventos de download disparados pelos cards de gráficos interativos (PNG / Excel)
  useEffect(() => {
    const handleExternalDownloadProgress = (e: Event) => {
      const custom = e as CustomEvent<{
        active?: boolean
        progress?: number
        statusMsg?: string
        stepDetail?: string
      }>
      if (!custom.detail) return
      const { active, progress, statusMsg, stepDetail } = custom.detail
      setIsExecutingTask(Boolean(active))
      if (typeof progress === 'number') setTaskProgressPct(progress)
      if (typeof statusMsg === 'string') setTaskStatusMsg(statusMsg)
      if (typeof stepDetail === 'string') setTaskStepDetail(stepDetail)
    }

    window.addEventListener('gid:gisa-download-progress', handleExternalDownloadProgress)
    return () => {
      window.removeEventListener('gid:gisa-download-progress', handleExternalDownloadProgress)
    }
  }, [])

  if (!isAllowedRole) {
    return null
  }

  // Drag handlers para o botão flutuante
  const handlePointerDown = (e: React.PointerEvent) => {
    isDraggingRef.current = true
    dragMovedRef.current = false
    dragStartYRef.current = e.clientY
    dragStartTopRef.current = topPosition
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDraggingRef.current) return
    const deltaY = e.clientY - dragStartYRef.current
    if (Math.abs(deltaY) > 6) {
      if (!dragMovedRef.current) {
        dragMovedRef.current = true
        try {
          ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
        } catch {}
      }
      const clamped = Math.max(
        60,
        Math.min(window.innerHeight - 85, dragStartTopRef.current + deltaY)
      )
      setTopPosition(clamped)
    }
  }

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!isDraggingRef.current) return
    isDraggingRef.current = false
    if (dragMovedRef.current) {
      try {
        ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
      } catch {}
      localStorage.setItem('@GID:gisa_top_pos', String(Math.round(topPosition)))
      setTimeout(() => {
        dragMovedRef.current = false
      }, 50)
    } else {
      const target = e.target as HTMLElement | null
      if (target && target.closest('button')) {
        setIsOpen(true)
        setShowTooltip(false)
      }
    }
  }

  const handleBubbleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (dragMovedRef.current) {
      dragMovedRef.current = false
      return
    }
    setIsOpen(true)
    setShowTooltip(false)
  }

  // ==========================================
  // NAVEGAÇÃO INTELIGENTE DO GID
  // ==========================================
  const handleActionNavigate = (route: string, pageId?: number, label?: string) => {
    setIsOpen(false)
    const lowerLabel = String(label || '').toLowerCase()
    const isTrainingAction =
      pageId === 14 && (lowerLabel.includes('treinar') || lowerLabel.includes('ensinar'))
    const isAiConfigAction =
      pageId === 14 &&
      !isTrainingAction &&
      (lowerLabel.includes('chave') || lowerLabel.includes('ia') || lowerLabel.includes('configurar'))
    const isEmailAction =
      pageId === 14 && (lowerLabel.includes('e-mail') || lowerLabel.includes('email'))

    if (pageId === 14 || route === '/integracoes') {
      const detailPayload = {
        tab: isEmailAction ? 'email' : 'gisa',
        openAiModal: isAiConfigAction,
        openTrainingModal: isTrainingAction,
      }
      try {
        sessionStorage.setItem('@GID:pending_integrations_action', JSON.stringify(detailPayload))
      } catch {}

      if (location.pathname !== '/painel' && location.pathname !== '/integracoes') {
        navigate('/painel')
      }
      window.dispatchEvent(new CustomEvent('gid:navigate-page', { detail: { pageId: 14 } }))
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent('gid:open-integrations-tab', { detail: detailPayload })
        )
      }, 100)
      return
    }

    if (typeof pageId === 'number') {
      if (location.pathname !== '/painel') {
        navigate('/painel')
      }
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('gid:navigate-page', { detail: { pageId } }))
      }, 80)
      return
    }
    navigate(route)
  }

  // ==========================================
  // HELPER: NORMALIZAR MATRIZ DE REGULARIDADE DOS ÚLTIMOS 7 DIAS (VISUAL + ESCRITO)
  // ==========================================
  const buildRegularidade7Dias = (summary: any) => {
    const weekDaysShort = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
    let diasCabecalho7d: Array<{
      dataIso: string
      labelColuna: string
      isDomingo: boolean
    }> = Array.isArray(summary?.diasCabecalho7d) ? summary.diasCabecalho7d : []

    if (diasCabecalho7d.length !== 7) {
      const nowRef = new Date()
      diasCabecalho7d = []
      for (let i = 6; i >= 0; i--) {
        const d = new Date(nowRef.getFullYear(), nowRef.getMonth(), nowRef.getDate() - i)
        const yyyy = d.getFullYear()
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        const wk = weekDaysShort[d.getDay()]
        diasCabecalho7d.push({
          dataIso: `${yyyy}-${mm}-${dd}`,
          labelColuna: `${dd}/${mm} (${wk})`,
          isDomingo: d.getDay() === 0,
        })
      }
    }

    const rawDists: any[] = Array.isArray(summary?.topDistribuidores)
      ? summary.topDistribuidores
      : []

    const distribuidores7d = rawDists.map((d: any) => {
      if (Array.isArray(d.ultimos7Dias) && d.ultimos7Dias.length === 7) {
        return d
      }

      const ultimoDt = d.ultimoEnvio ? new Date(d.ultimoEnvio) : null
      const ultimoIso =
        ultimoDt && !isNaN(ultimoDt.getTime())
          ? `${ultimoDt.getFullYear()}-${String(ultimoDt.getMonth() + 1).padStart(
              2,
              '0'
            )}-${String(ultimoDt.getDate()).padStart(2, '0')}`
          : null

      let diasEnviados7d = 0
      let diasPendentes7d = 0
      let diasUteis7d = 0

      const ultimos7Dias = diasCabecalho7d.map((dia) => {
        const isCovered = Boolean(ultimoIso && dia.dataIso <= ultimoIso)
        let status: 'enviado' | 'pendente' | 'justificado' | 'domingo' = 'pendente'
        let bolinha = '🔴'
        let escrito = 'Pendente'

        if (!dia.isDomingo && isCovered) {
          status = 'enviado'
          bolinha = '🟢'
          escrito = 'Enviado'
          diasEnviados7d++
          diasUteis7d++
        } else if (dia.isDomingo) {
          status = 'domingo'
          bolinha = '⚪'
          escrito = 'Domingo'
        } else {
          status = 'pendente'
          bolinha = '🔴'
          escrito = 'Pendente'
          diasPendentes7d++
          diasUteis7d++
        }

        return {
          dataIso: dia.dataIso,
          labelColuna: dia.labelColuna,
          status,
          bolinha,
          escrito,
          celulaCompleta: `${bolinha} ${escrito}`,
        }
      })

      const pct = diasUteis7d > 0 ? Math.round((diasEnviados7d / diasUteis7d) * 100) : 100
      let situacaoRegularidade: 'regular' | 'parcial' | 'irregular' = 'irregular'
      let statusRegularidadeEscrito = `🔴 IRREGULAR / PENDENTE (0/${diasUteis7d} dias úteis — 0%)`

      if (diasPendentes7d === 0 || pct >= 85) {
        situacaoRegularidade = 'regular'
        statusRegularidadeEscrito = `🟢 REGULAR — Em dia (${diasEnviados7d}/${diasUteis7d} dias úteis — ${pct}%)`
      } else if (diasEnviados7d > 0) {
        situacaoRegularidade = 'parcial'
        statusRegularidadeEscrito = `🟠 REGULAR PARCIAL — Atenção (${diasEnviados7d}/${diasUteis7d} dias úteis — ${pct}%)`
      }

      return {
        ...d,
        ultimos7Dias,
        graficoBolinhas7d: ultimos7Dias.map((u) => u.bolinha).join(' '),
        diasEnviados7d,
        diasPendentes7d,
        diasUteis7d,
        percentualRegularidade7d: pct,
        situacaoRegularidade,
        statusRegularidadeEscrito,
      }
    })

    return { diasCabecalho7d, distribuidores7d }
  }

  const waitDelay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

  // ==========================================
  // TAREFA 1: GERAR E BAIXAR PDF EXECUTIVO DO GID (FOCADO NO ASSUNTO ATUAL OU GERAL)
  // ==========================================
  const executeGerarPdf = async (escopoCustom?: string, referenceMsg?: ChatMessage) => {
    const lowerEscopo = String(escopoCustom || '').toLowerCase()
    const activeChartRef = referenceMsg?.reportData || referenceMsg?.chartData
    const refTitleLower = String(activeChartRef?.title || '').toLowerCase()
    const refTextLower = String(referenceMsg?.text || '').toLowerCase()
    const isEscopoResumoGeral =
      lowerEscopo.includes('resumo executivo') ||
      lowerEscopo.includes('relatório executivo') ||
      lowerEscopo.includes('relatorio executivo')

    const isFocoLevantamentoDist =
      !isEscopoResumoGeral &&
      (lowerEscopo.includes('levantamento') ||
        (lowerEscopo.includes('distribuidor') &&
          (lowerEscopo.includes('uf') ||
            lowerEscopo.includes('telefone') ||
            lowerEscopo.includes('cadastrado') ||
            lowerEscopo.includes('cadastro'))) ||
        refTitleLower.includes('levantamento') ||
        refTitleLower.includes('distribuidores ativos') ||
        refTextLower.includes('levantamento cadastral de distribuidores'))

    const isFocoRegularidade =
      !isEscopoResumoGeral &&
      !isFocoLevantamentoDist &&
      (lowerEscopo.includes('regularidade') ||
        lowerEscopo.includes('auditar envios') ||
        lowerEscopo.includes('7 dias') ||
        lowerEscopo.includes('calendario') ||
        lowerEscopo.includes('calendário') ||
        refTitleLower.includes('regularidade') ||
        refTextLower.includes('auditoria de regularidade'))

    const isFocoSellout =
      !isEscopoResumoGeral &&
      !isFocoLevantamentoDist &&
      !isFocoRegularidade &&
      (lowerEscopo.includes('sellout') ||
        lowerEscopo.includes('faturamento') ||
        lowerEscopo.includes('evolucao') ||
        lowerEscopo.includes('evolução') ||
        refTitleLower.includes('sellout') ||
        refTextLower.includes('evolução de sellout') ||
        refTextLower.includes('ranking de sellout'))

    setIsExecutingTask(true)
    setTaskProgressPct(14)
    setTaskStatusMsg(
      isFocoRegularidade
        ? 'Gerando Relatório PDF apenas de Regularidade (7 Dias)...'
        : isFocoLevantamentoDist
        ? 'Gerando Relatório PDF do Levantamento de Distribuidores...'
        : isFocoSellout
        ? 'Gerando Relatório PDF apenas de Sellout...'
        : 'Gerando Relatório Executivo em PDF...'
    )
    setTaskStepDetail('Etapa 1/4: Conectando à base SQL oficial do GID...')
    toast.info('GID IA: Gerando documento PDF solicitado...')

    let progressTimer: ReturnType<typeof setInterval> | null = setInterval(() => {
      setTaskProgressPct((prev) => {
        if (prev < 36) {
          setTaskStepDetail('Etapa 2/4: Extraindo indicadores do módulo solicitado...')
          return prev + 8
        }
        if (prev < 64) {
          setTaskStepDetail('Etapa 2/4: Formatando tabelas para impressão A4...')
          return prev + 6
        }
        return prev
      })
    }, 320)

    try {
      const res = await api.get('/integracoes/gisa/resumo')
      if (progressTimer) {
        clearInterval(progressTimer)
        progressTimer = null
      }

      setTaskProgressPct(76)
      setTaskStepDetail('Etapa 3/4: Montando documento PDF corporativo...')
      await waitDelay(180)

      const summary = res.data?.summary
      const kpis = summary?.kpis || {}
      const { diasCabecalho7d, distribuidores7d } = buildRegularidade7Dias(summary)
      const sellDist = summary?.selloutPorDistribuidor || []

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
      const dateStr = new Date().toLocaleDateString('pt-BR')

      // Cabeçalho Corporativo GID Executivo (Navy #001842 + Lime #A6C10B + Sky #0284C7)
      doc.setFillColor(0, 24, 66)
      doc.rect(0, 0, 210, 36, 'F')
      doc.setFillColor(2, 132, 199)
      doc.rect(0, 0, 210, 2.2, 'F')
      doc.setFillColor(166, 193, 11)
      doc.rect(0, 34.2, 210, 1.8, 'F')

      // Selo GID IA no canto superior direito
      doc.setFillColor(15, 41, 66)
      doc.setDrawColor(56, 189, 248)
      doc.roundedRect(172, 6, 24, 15, 2, 2, 'FD')
      doc.setFontSize(8.5)
      doc.setTextColor(56, 189, 248)
      doc.text('GID IA', 177.5, 12.5)
      doc.setFontSize(6)
      doc.setTextColor(148, 163, 184)
      doc.text('GRUPO GSA', 176.2, 17.5)

      doc.setFontSize(7.5)
      doc.setTextColor(166, 193, 11)
      doc.text('SISTEMA DE GESTAO DE INFORMACOES DE DISTRIBUIDORES - GRUPO GSA', 14, 9.5)

      doc.setTextColor(255, 255, 255)
      doc.setFontSize(13.5)
      doc.text(
        isFocoRegularidade
          ? 'RELATORIO DE REGULARIDADE DE ENVIOS (7 DIAS)'
          : isFocoLevantamentoDist
          ? 'LEVANTAMENTO CADASTRAL DE DISTRIBUIDORES'
          : isFocoSellout
          ? 'RELATORIO EXECUTIVO DE SELLOUT E FATURAMENTO'
          : 'RELATORIO EXECUTIVO GERAL (SELLOUT & FATURAMENTO)',
        14,
        17.5
      )
      doc.setFontSize(8.5)
      doc.setTextColor(203, 213, 225)
      doc.text(
        `Escopo: ${
          isFocoRegularidade
            ? 'Auditoria Exclusiva de Regularidade (Ultimos 7 Dias)'
            : isFocoLevantamentoDist
            ? 'Relacao de Distribuidores (Cod. Dist, Nome, UF Principal, Regional e Telefone)'
            : isFocoSellout
            ? 'Consolidado Exclusivo de Sellout'
            : escopoCustom || 'Panorama Executivo (Sellout & Faturamento)'
        } | Data: ${dateStr}`,
        14,
        24.5
      )
      doc.text(
        `Destinatario: ${summary?.userName || user?.name || 'Gestor'} (${(
          summary?.userRole ||
          userRole ||
          'GID'
        ).toUpperCase()})`,
        14,
        30.5
      )

      // 4 Cards Executivos de KPIs no Topo
      const kpiCards = [
        {
          title: 'FATURAMENTO SELLOUT',
          value: Number(kpis.selloutUltimos30DiasValor || 0).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }),
          sub: `Vol: ${Number(kpis.selloutUltimos30DiasQtd || 0).toLocaleString('pt-BR')} un/cx`,
          accent: [16, 185, 129] as [number, number, number],
        },
        {
          title: 'DISTRIBUIDORES ATIVOS',
          value: `${Number(kpis.totalDistribuidoresAtivos || distribuidores7d.length || 0)} Unidades`,
          sub: isFocoRegularidade
            ? `${Number(kpis.distribuidoresEmDia7d || 0)} em dia | ${Number(kpis.distribuidoresPendentes7d || 0)} pend.`
            : 'Monitoramento GID Ativo',
          accent: [2, 132, 199] as [number, number, number],
        },
        {
          title: 'CARTEIRA DE CLIENTES',
          value: `${Number(kpis.totalClientesAtivos || 0).toLocaleString('pt-BR')} PDVs`,
          sub: 'Pontos de venda ativos',
          accent: [99, 102, 241] as [number, number, number],
        },
        {
          title: 'ESTOQUE & CATALOGO',
          value: `${Number(kpis.totalProdutosHomologados || 0).toLocaleString('pt-BR')} SKUs`,
          sub: `Est: ${Number(kpis.estoqueRegistrosRecentes || 0).toLocaleString('pt-BR')} reg.`,
          accent: [245, 158, 11] as [number, number, number],
        },
      ]

      const cardW = 43.5
      const cardGap = 2.6
      kpiCards.forEach((card, idx) => {
        const cx = 14 + idx * (cardW + cardGap)
        const cy = 41
        doc.setFillColor(248, 250, 252)
        doc.setDrawColor(226, 232, 240)
        doc.roundedRect(cx, cy, cardW, 19, 1.5, 1.5, 'FD')
        doc.setFillColor(card.accent[0], card.accent[1], card.accent[2])
        doc.rect(cx, cy, 1.6, 19, 'F')
        doc.setFontSize(6.3)
        doc.setTextColor(100, 116, 139)
        doc.text(card.title, cx + 3.5, cy + 5)
        doc.setFontSize(8.8)
        doc.setTextColor(15, 23, 42)
        doc.text(String(card.value).slice(0, 22), cx + 3.5, cy + 11.5)
        doc.setFontSize(6.3)
        doc.setTextColor(71, 85, 105)
        doc.text(String(card.sub).slice(0, 26), cx + 3.5, cy + 16.5)
      })

      let currentStartY = 65

      if (isFocoLevantamentoDist) {
        const totalPdvsLev = distribuidores7d.reduce(
          (acc: number, d: any) => acc + Number(d.totalPdvsUf || 0),
          0
        )
        autoTable(doc, {
          startY: currentStartY,
          head: [
            [
              'Cod. Dist',
              'Distribuidor Cadastrado',
              'UF Principal',
              'Regional',
              'Telefone Cadastrado',
              'Responsavel',
              'PDVs na UF',
            ],
          ],
          body: distribuidores7d.map((d: any) => [
            String(d.codDist || d.cod_dist || d.id),
            String(d.nome || '-').slice(0, 28),
            String(d.ufPrincipal || d.regional || '-'),
            String(d.regional || 'Geral'),
            String(d.telefone || 'Nao informado'),
            String(d.responsavel || '-').slice(0, 18),
            Number(d.totalPdvsUf || 0).toLocaleString('pt-BR'),
          ]),
          foot: [
            [
              'TOTAL',
              `${distribuidores7d.length} Distribuidores Ativos`,
              '-',
              '-',
              '-',
              '-',
              totalPdvsLev.toLocaleString('pt-BR'),
            ],
          ],
          headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255] },
          footStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          styles: { fontSize: 8 },
        })
      } else if (isFocoRegularidade) {
        autoTable(doc, {
          startY: currentStartY,
          head: [
            [
              'Cod.',
              'Distribuidor',
              'Reg.',
              ...diasCabecalho7d.map((dh) => dh.labelColuna.split(' ')[0]),
              'Situacao (Por Escrito)',
              'Ult. Envio',
            ],
          ],
          body: distribuidores7d.map((d: any) => [
            String(d.codDist || d.cod_dist || d.id),
            String(d.nome || '-').slice(0, 22),
            String(d.regional || '-'),
            ...(d.ultimos7Dias || []).map((u: any) =>
              u.status === 'enviado'
                ? 'ENV'
                : u.status === 'justificado'
                ? 'JUST'
                : u.status === 'domingo'
                ? 'DOM'
                : 'PEND'
            ),
            String(d.statusRegularidadeEscrito || '-')
              .replace(/[🟢🔴🟠⚪]/g, '')
              .trim(),
            d.ultimoEnvio ? new Date(d.ultimoEnvio).toLocaleDateString('pt-BR') : 'Pendente',
          ]),
          headStyles: { fillColor: [15, 23, 42], halign: 'center' },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          styles: { fontSize: 7.2, valign: 'middle' },
          didParseCell: (hookData: any) => {
            if (hookData.section === 'body') {
              const colIdx = hookData.column.index
              const rawText = String(hookData.cell.raw || '')
              if (colIdx >= 3 && colIdx < 3 + diasCabecalho7d.length) {
                hookData.cell.styles.halign = 'center'
                hookData.cell.styles.fontStyle = 'bold'
                hookData.cell.styles.textColor = [255, 255, 255]
                if (rawText === 'ENV') {
                  hookData.cell.styles.fillColor = [69, 206, 1]
                } else if (rawText === 'JUST') {
                  hookData.cell.styles.fillColor = [255, 132, 0]
                } else if (rawText === 'DOM') {
                  hookData.cell.styles.fillColor = [167, 167, 167]
                } else {
                  hookData.cell.styles.fillColor = [214, 6, 6]
                }
              } else if (colIdx === 3 + diasCabecalho7d.length) {
                hookData.cell.styles.fontStyle = 'bold'
                if (rawText.startsWith('REGULAR —')) {
                  hookData.cell.styles.textColor = [22, 101, 52]
                } else if (rawText.startsWith('REGULAR PARCIAL')) {
                  hookData.cell.styles.textColor = [180, 83, 9]
                } else {
                  hookData.cell.styles.textColor = [185, 28, 28]
                }
              }
            }
          },
        })
      } else if (isFocoSellout) {
        const selloutSourceRows =
          Array.isArray(activeChartRef?.data) && activeChartRef!.data.length > 0
            ? activeChartRef!.data
            : sellDist
        const totalFatSell = selloutSourceRows.reduce(
          (acc: number, s: any) => acc + Number(s.Faturamento || 0),
          0
        )
        const totalVolSell = selloutSourceRows.reduce(
          (acc: number, s: any) => acc + Number(s.Volume || 0),
          0
        )
        autoTable(doc, {
          startY: currentStartY,
          head: [['#', 'Competencia / Distribuidor', 'Faturamento (R$)', 'Volume', 'Share (%)']],
          body: selloutSourceRows.map((s: any, idx: number) => {
            const fat = Number(s.Faturamento || 0)
            const share = totalFatSell > 0 ? ((fat / totalFatSell) * 100).toFixed(1) : '0.0'
            return [
              `${idx + 1}º`,
              String(s.label || '-'),
              fat.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }),
              Number(s.Volume || 0).toLocaleString('pt-BR'),
              `${share}%`,
            ]
          }),
          foot: [
            [
              'TOTAL',
              `${selloutSourceRows.length} Registros`,
              totalFatSell.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
              totalVolSell.toLocaleString('pt-BR'),
              '100.0%',
            ],
          ],
          headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255] },
          footStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          styles: { fontSize: 8.5 },
        })
      } else {
        // Relatório Executivo Geral (Sellout & Faturamento — sem Regularidade)
        const totalFatExec =
          sellDist.reduce((acc: number, s: any) => acc + Number(s.Faturamento || 0), 0) ||
          Number(kpis.selloutUltimos30DiasValor || 0)
        const totalVolExec =
          sellDist.reduce((acc: number, s: any) => acc + Number(s.Volume || 0), 0) ||
          Number(kpis.selloutUltimos30DiasQtd || 0)

        const distLookup = new Map<string, any>()
        distribuidores7d.forEach((d: any) => {
          distLookup.set(String(d.nome || '').toLowerCase().trim(), d)
        })

        autoTable(doc, {
          startY: currentStartY,
          head: [
            [
              '#',
              'Cod. Dist',
              'Distribuidor',
              'UF / Reg.',
              'Faturamento Sellout (R$)',
              'Volume (Qtd)',
              'Share (%)',
            ],
          ],
          body: sellDist.map((s: any, idx: number) => {
            const matched = distLookup.get(String(s.label || '').toLowerCase().trim())
            const codDist = matched?.codDist || matched?.cod_dist || s.codDist || '-'
            const ufReg = matched?.ufPrincipal
              ? `${matched.ufPrincipal} (${matched.regional || 'Geral'})`
              : matched?.regional || 'Geral'
            const fat = Number(s.Faturamento || 0)
            const share = totalFatExec > 0 ? ((fat / totalFatExec) * 100).toFixed(1) : '0.0'
            return [
              `${idx + 1}º`,
              String(codDist),
              String(s.label || '-'),
              String(ufReg),
              fat.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }),
              Number(s.Volume || 0).toLocaleString('pt-BR'),
              `${share}%`,
            ]
          }),
          foot: [
            [
              'TOTAL',
              '-',
              `CONSOLIDADO (${sellDist.length} DISTRIBUIDORES)`,
              '-',
              totalFatExec.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }),
              totalVolExec.toLocaleString('pt-BR'),
              '100.0%',
            ],
          ],
          headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255] },
          footStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          styles: { fontSize: 8.2, valign: 'middle' },
        })
      }

      // Rodapé Corporativo Numerado em todas as páginas
      const totalPages = (doc as any).internal.getNumberOfPages()
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p)
        doc.setDrawColor(203, 213, 225)
        doc.line(14, 286, 196, 286)
        doc.setFontSize(7.5)
        doc.setTextColor(100, 116, 139)
        doc.text(
          'GID - Sistema de Gestao de Informacoes de Distribuidores (Grupo GSA) | Relatorio Oficial Auditavel',
          14,
          291
        )
        doc.text(`Pagina ${p} de ${totalPages}`, 176, 291)
      }

      setTaskProgressPct(92)
      setTaskStepDetail('Etapa 4/4: Finalizando arquivo PDF para download no navegador...')
      await waitDelay(160)

      const cleanDate = dateStr.replace(/\//g, '-')
      const fileName = isFocoRegularidade
        ? `Relatorio_Regularidade_7Dias_GID_${cleanDate}.pdf`
        : isFocoLevantamentoDist
        ? `Levantamento_Distribuidores_GID_${cleanDate}.pdf`
        : isFocoSellout
        ? `Relatorio_Sellout_GID_${cleanDate}.pdf`
        : `Relatorio_Executivo_GID_${cleanDate}.pdf`
      doc.save(fileName)

      setTaskProgressPct(100)
      setTaskStatusMsg('Relatório PDF concluído!')
      setTaskStepDetail(`Download iniciado: ${fileName}`)
      toast.success('Relatório PDF gerado e baixado com sucesso!')

      const gisaMsg: ChatMessage = {
        id: String(Date.now()),
        sender: 'gisa',
        text:
          `📄 **Documento PDF gerado com sucesso!**\n\n` +
          `• **Arquivo**: \`${fileName}\`\n` +
          `• **Conteúdo Exclusivo**: ${
            isFocoRegularidade
              ? 'Matriz de Regularidade de Envios (Últimos 7 Dias)'
              : isFocoLevantamentoDist
              ? 'Levantamento Cadastral de Distribuidores (Nome, UF Principal e Telefone)'
              : isFocoSellout
              ? 'Consolidado de Sellout e Faturamento'
              : 'Panorama Executivo GID'
          }\n\n` +
          `O download do PDF já foi iniciado no seu navegador!`,
        actionButton: isFocoRegularidade
          ? { label: 'Abrir Calendário de Regularidade', route: '/painel', pageId: 1 }
          : isFocoLevantamentoDist
          ? { label: 'Abrir Gestão de Unidades / Distribuidores', route: '/painel', pageId: 6 }
          : { label: 'Abrir Central de Relatórios', route: '/painel', pageId: 8 },
        followUps: isFocoRegularidade
          ? [
              '📊 Exportar relatório Excel apenas de Regularidade (.xlsx)',
              '📈 Quero ver as regularidades com gráficos',
              '🔴 Listar apenas os distribuidores pendentes (> 7 dias)',
            ]
          : isFocoLevantamentoDist
          ? [
              '📊 Exportar Levantamento de Distribuidores em Excel (.xlsx)',
              '📈 Quero ver o Levantamento de Distribuidores com gráficos',
            ]
          : isFocoSellout
          ? [
              '📊 Exportar planilha Excel apenas de Sellout (.xlsx)',
              '📈 Quero ver o Sellout com gráficos',
            ]
          : ['📊 Baixar Planilha Excel (.xlsx)'],
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      }
      setMessages((prev) => [...prev, gisaMsg])
      await waitDelay(700)
    } catch (err: any) {
      if (progressTimer) {
        clearInterval(progressTimer)
        progressTimer = null
      }
      const msg =
        err?.response?.data?.error ||
        'Vincule e ative uma chave de IA na aba "Chat & Chaves GID IA" para liberar a coleta executiva.'
      toast.error(msg)
      setMessages((prev) => [
        ...prev,
        {
          id: String(Date.now()),
          sender: 'gisa',
          text: `⚠️ **Não foi possível gerar o PDF agora:**\n\n${msg}`,
          actionButton: {
            label: 'Ir para Integrações & IA GISA',
            route: '/painel',
            pageId: 14,
          },
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ])
    } finally {
      if (progressTimer) clearInterval(progressTimer)
      setIsExecutingTask(false)
      setTaskProgressPct(0)
      setTaskStatusMsg('')
      setTaskStepDetail('')
    }
  }

  // ==========================================
  // TAREFA 2: GERAR E BAIXAR PLANILHA EXCEL (.XLSX FOCADO NA SOLICITAÇÃO OU MULTI-ABAS)
  // ==========================================
  const executeGerarExcel = async (escopoCustom?: string, referenceMsg?: ChatMessage) => {
    const lowerEscopo = String(escopoCustom || '').toLowerCase()
    const activeChartRef = referenceMsg?.reportData || referenceMsg?.chartData
    const refTitleLower = String(activeChartRef?.title || '').toLowerCase()
    const refTextLower = String(referenceMsg?.text || '').toLowerCase()
    const pediuGraficoNoChat =
      lowerEscopo.includes('grafico') ||
      lowerEscopo.includes('gráfico') ||
      lowerEscopo.includes('chart')

    const isEscopoResumoGeral =
      lowerEscopo.includes('resumo executivo') ||
      lowerEscopo.includes('relatório executivo') ||
      lowerEscopo.includes('relatorio executivo')

    const isReferencingLastChat =
      !isEscopoResumoGeral &&
      (Boolean(referenceMsg) ||
        lowerEscopo.includes('essas informac') ||
        lowerEscopo.includes('essas informaç') ||
        lowerEscopo.includes('estas informac') ||
        lowerEscopo.includes('estas informaç') ||
        lowerEscopo.includes('esses dados') ||
        lowerEscopo.includes('estes dados') ||
        lowerEscopo.includes('esse levantamento') ||
        lowerEscopo.includes('essa lista') ||
        lowerEscopo.includes('deste sellout') ||
        lowerEscopo.includes('isso') ||
        lowerEscopo.includes('do chat') ||
        lowerEscopo.includes('ficou no chat'))

    const isFocoLevantamentoDist =
      !isEscopoResumoGeral &&
      (lowerEscopo.includes('levantamento') ||
        (lowerEscopo.includes('distribuidor') &&
          (lowerEscopo.includes('uf') ||
            lowerEscopo.includes('estado') ||
            lowerEscopo.includes('telefone') ||
            lowerEscopo.includes('contato') ||
            lowerEscopo.includes('cadastrado') ||
            lowerEscopo.includes('cadastro') ||
            lowerEscopo.includes('nome'))) ||
        refTitleLower.includes('levantamento') ||
        refTitleLower.includes('distribuidores ativos') ||
        refTextLower.includes('levantamento cadastral de distribuidores'))

    const isFocoRegularidade =
      !isEscopoResumoGeral &&
      !isFocoLevantamentoDist &&
      (lowerEscopo.includes('regularidade') ||
        lowerEscopo.includes('auditar envios') ||
        lowerEscopo.includes('7 dias') ||
        lowerEscopo.includes('calendario') ||
        lowerEscopo.includes('calendário') ||
        refTitleLower.includes('regularidade') ||
        refTextLower.includes('auditoria de regularidade'))

    const isFocoEstoque =
      !isEscopoResumoGeral &&
      !isFocoLevantamentoDist &&
      !isFocoRegularidade &&
      (lowerEscopo.includes('estoque') ||
        refTitleLower.includes('estoque') ||
        refTextLower.includes('posição de estoque'))

    const isFocoProdutosInvalidos =
      !isEscopoResumoGeral &&
      !isFocoLevantamentoDist &&
      !isFocoRegularidade &&
      !isFocoEstoque &&
      (lowerEscopo.includes('invalido') || lowerEscopo.includes('inválido'))

    const isFocoSellout =
      !isEscopoResumoGeral &&
      !isFocoLevantamentoDist &&
      !isFocoRegularidade &&
      !isFocoEstoque &&
      !isFocoProdutosInvalidos &&
      (lowerEscopo.includes('sellout') ||
        lowerEscopo.includes('faturamento') ||
        lowerEscopo.includes('evolucao') ||
        lowerEscopo.includes('evolução') ||
        refTitleLower.includes('sellout') ||
        refTextLower.includes('evolução de sellout') ||
        refTextLower.includes('ranking de sellout'))

    const isFocoEspecifico =
      isFocoLevantamentoDist ||
      isFocoRegularidade ||
      isFocoEstoque ||
      isFocoProdutosInvalidos ||
      isFocoSellout ||
      (isReferencingLastChat && Boolean(activeChartRef?.data?.length))

    setIsExecutingTask(true)
    setTaskProgressPct(15)
    setTaskStatusMsg(
      isFocoLevantamentoDist
        ? 'Gerando Planilha Excel — Apenas Levantamento de Distribuidores...'
        : isFocoRegularidade
        ? 'Gerando Planilha Excel — Apenas Regularidade de Envios (7 Dias)...'
        : isFocoSellout
        ? 'Gerando Planilha Excel — Apenas Sellout...'
        : isFocoEstoque
        ? 'Gerando Planilha Excel — Apenas Posição de Estoque...'
        : 'Gerando Planilha Corporativa GID (.xlsx)...'
    )
    setTaskStepDetail('Etapa 1/4: Consultando base SQL do módulo solicitado...')
    toast.info(
      isFocoRegularidade
        ? 'GID IA: Gerando Planilha Excel exclusiva de Regularidade (7 Dias)...'
        : isFocoLevantamentoDist
        ? 'GID IA: Gerando Planilha Excel exclusiva do Levantamento de Distribuidores...'
        : isFocoSellout
        ? 'GID IA: Gerando Planilha Excel exclusiva de Sellout...'
        : 'GID IA: Gerando planilha Excel corporativa do GID...'
    )

    let progressTimer: ReturnType<typeof setInterval> | null = setInterval(() => {
      setTaskProgressPct((prev) => {
        if (prev < 38) {
          setTaskStepDetail('Etapa 2/4: Estruturando registros e colunas do relatório...')
          return prev + 8
        }
        if (prev < 64) {
          setTaskStepDetail('Etapa 2/4: Aplicando formatação corporativa GID...')
          return prev + 6
        }
        return prev
      })
    }, 320)

    try {
      const res = await api.get('/integracoes/gisa/resumo')
      if (progressTimer) {
        clearInterval(progressTimer)
        progressTimer = null
      }

      setTaskProgressPct(75)
      setTaskStepDetail('Etapa 3/4: Estilizando planilha com padrão contábil GID...')
      await waitDelay(180)

      const summary = res.data?.summary
      const kpis = summary?.kpis || {}

      const wb = XLSX.utils.book_new()
      const { diasCabecalho7d, distribuidores7d } = buildRegularidade7Dias(summary)

      // 1. Constrói aba de Levantamento Cadastral de Distribuidores (SEM colunas de Regularidade a menos que solicitado)
      let wsLevantamentoBuilt: any = null
      if (distribuidores7d.length > 0 && (isFocoLevantamentoDist || !isFocoEspecifico)) {
        const refRowsByLabel = new Map<string, any>()
        if (Array.isArray(activeChartRef?.data)) {
          activeChartRef!.data.forEach((r: any) => {
            if (r?.label) refRowsByLabel.set(String(r.label).trim().toUpperCase(), r)
          })
        }

        const selloutByLabel = new Map<string, { faturamento: number; volume: number }>()
        if (Array.isArray(summary?.selloutPorDistribuidor)) {
          summary.selloutPorDistribuidor.forEach((s: any) => {
            if (s?.label) {
              selloutByLabel.set(String(s.label).trim().toUpperCase(), {
                faturamento: Number(s.Faturamento) || 0,
                volume: Number(s.Volume) || 0,
              })
            }
          })
        }

        let totalPdvsUfSoma = 0
        let totalFaturamentoLevantamento = 0

        const levantamentoRows = distribuidores7d.map((d: any, rowIdx: number) => {
          const isEven = rowIdx % 2 === 1
          const keyUpper = String(d.nome || '').trim().toUpperCase()
          const refRow = refRowsByLabel.get(keyUpper)
          const sellRow = selloutByLabel.get(keyUpper)

          const codDistVal = String(refRow?.Cod_Dist || d.codDist || d.cod_dist || d.id)
          const ufVal = refRow?.UF_Principal || d.ufPrincipal || d.regional || 'Não informada'
          const telVal =
            refRow?.Telefone ||
            (d.telefone && String(d.telefone).trim().length > 0
              ? String(d.telefone).trim()
              : 'Não informado')
          const respVal =
            refRow?.Responsavel ||
            (d.responsavel && String(d.responsavel).trim().length > 0
              ? String(d.responsavel).trim()
              : '—')
          const emailVal =
            d.email && String(d.email).trim().length > 0 ? String(d.email).trim() : '—'
          const pdvsUfVal = Number(
            refRow?.PDVs_UF_Principal ?? refRow?.PDVs_Ativos_Mes ?? d.totalPdvsUf ?? 0
          )
          const fatVal = Number(refRow?.Faturamento ?? sellRow?.faturamento ?? 0)

          totalPdvsUfSoma += pdvsUfVal
          totalFaturamentoLevantamento += fatVal

          return [
            cellText(codDistVal, isEven, 'center', true),
            cellText(d.nome || '—', isEven, 'left', true),
            cellText(d.razaoSocial || d.nome || '—', isEven, 'left'),
            cellText(String(ufVal), isEven, 'center', true),
            cellText(d.regional || 'Geral', isEven, 'center'),
            cellText(String(telVal), isEven, 'center', true),
            cellText(String(respVal), isEven, 'left'),
            cellText(String(emailVal), isEven, 'left'),
            cellNumber(pdvsUfVal, isEven),
            cellMoney(fatVal, isEven, true),
          ]
        })

        wsLevantamentoBuilt = buildCorporateSheet({
          reportTitle:
            activeChartRef?.title && refTitleLower.includes('levantamento')
              ? activeChartRef.title
              : 'Levantamento Cadastral de Distribuidores Ativos',
          subtitle:
            'RELATÓRIO: RELAÇÃO DE DISTRIBUIDORES CADASTRADOS (CÓD. DIST, NOME, RAZÃO SOCIAL, UF PRINCIPAL, REGIONAL E TELEFONE)',
          periodLabel:
            activeChartRef?.subtitle ||
            `Base Oficial GID • Extraído em ${new Date().toLocaleDateString('pt-BR')}`,
          distributorLabel: `${distribuidores7d.length} Distribuidores Ativos`,
          headers: [
            { label: 'Cód. Dist', align: 'center' },
            { label: 'Nome Cadastrado (Fantasia)', align: 'left' },
            { label: 'Razão Social', align: 'left' },
            { label: 'UF Principal', align: 'center' },
            { label: 'Regional', align: 'center' },
            { label: 'Telefone Cadastrado', align: 'center' },
            { label: 'Responsável', align: 'left' },
            { label: 'E-mail Cadastrado', align: 'left' },
            { label: 'PDVs na UF / Mês', align: 'right' },
            { label: 'Faturamento Sellout (R$)', align: 'right' },
          ],
          dataRows: levantamentoRows,
          totalsRow: [
            'TOTAL',
            `${distribuidores7d.length} Distribuidores Cadastrados`,
            '—',
            '—',
            '—',
            '—',
            '—',
            '—',
            totalPdvsUfSoma,
            totalFaturamentoLevantamento,
          ],
        })
      }

      // 2. Constrói aba de Regularidade de Envios SOMENTE quando o usuário solicitou Regularidade
      let wsRegBuilt: any = null
      if (distribuidores7d.length > 0 && isFocoRegularidade) {
        let totalDiasEnviadosMalha = 0
        let totalDiasPendentesMalha = 0

        const regRows = distribuidores7d.map((d: any, rowIdx: number) => {
          const isEven = rowIdx % 2 === 1
          totalDiasEnviadosMalha += Number(d.diasEnviados7d || 0)
          totalDiasPendentesMalha += Number(d.diasPendentes7d || 0)

          const dayCells = (d.ultimos7Dias || []).map((u: any) =>
            cellStatusBadge(
              String(u.celulaCompleta || '🔴 Pendente'),
              u.status || 'pendente'
            )
          )

          return [
            cellText(String(d.codDist || d.cod_dist || d.id), isEven, 'center', true),
            cellText(d.nome, isEven, 'left', true),
            cellText(d.regional || 'Geral', isEven, 'center'),
            ...dayCells,
            cellText(d.graficoBolinhas7d || '—', isEven, 'center', true),
            cellNumber(Number(d.diasEnviados7d || 0), isEven, true),
            cellNumber(Number(d.diasPendentes7d || 0), isEven, true),
            cellStatusBadge(
              String(d.statusRegularidadeEscrito || '🔴 IRREGULAR / PENDENTE'),
              d.situacaoRegularidade || 'irregular'
            ),
            cellText(
              d.ultimoEnvio
                ? new Date(d.ultimoEnvio).toLocaleDateString('pt-BR')
                : 'Sem Envio Recente',
              isEven,
              'center'
            ),
          ]
        })

        wsRegBuilt = buildCorporateSheet({
          reportTitle: 'Auditoria de Regularidade de Envios — Últimos 7 Dias',
          subtitle:
            'RELATÓRIO: GRÁFICO DE REGULARIDADE DOS ÚLTIMOS 7 DIAS (VISUAL DE BOLAS VERDES/VERMELHAS & DIAGNÓSTICO POR ESCRITO)',
          meta: `Legenda: [🟢 Verde = Enviado]  [🔴 Vermelho = Pendente]  [🟠 Laranja = Justificado]  [⚪ Cinza = Domingo]   |   Extraído em: ${new Date().toLocaleString('pt-BR')}`,
          headers: [
            { label: 'Cód. Dist', align: 'center' },
            { label: 'Distribuidor', align: 'left' },
            { label: 'Regional / UF', align: 'center' },
            ...diasCabecalho7d.map((dh) => ({
              label: dh.labelColuna,
              align: 'center' as const,
            })),
            { label: 'Gráfico 7 Dias (Visual)', align: 'center' },
            { label: 'Enviados (7d)', align: 'right' },
            { label: 'Pendentes (7d)', align: 'right' },
            { label: 'Situação Regular (Por Escrito)', align: 'center' },
            { label: 'Data do Último Envio', align: 'center' },
          ],
          dataRows: regRows,
          totalsRow: [
            'RESUMO',
            `${distribuidores7d.length} Distribuidores Auditados`,
            '—',
            ...diasCabecalho7d.map((dh) => dh.labelColuna.split(' ')[0]),
            '🟢 Enviado | 🔴 Pendente',
            totalDiasEnviadosMalha,
            totalDiasPendentesMalha,
            `Em dia: ${kpis.distribuidoresComEnvioRecente || 0} | Pendentes: ${
              kpis.distribuidoresPendentes7Dias || 0
            }`,
            'Consolidado 7d',
          ],
        })
      }

      // ==============================================================
      // MONTAGEM ESTRITA DO WORKBOOK:
      // Se o usuário pediu um relatório específico (ex: só Regularidade, só Levantamento, só Sellout, só Estoque),
      // gera SOMENTE a aba solicitada, sem misturar abas de outros assuntos!
      // E se pediu o Relatório Executivo Geral, NUNCA inclui Regularidade sem solicitação!
      // ==============================================================
      if (isFocoRegularidade && wsRegBuilt) {
        XLSX.utils.book_append_sheet(wb, wsRegBuilt, 'Regularidade_Envios')
      } else if (isFocoLevantamentoDist && wsLevantamentoBuilt) {
        XLSX.utils.book_append_sheet(wb, wsLevantamentoBuilt, 'Levantamento_Distribuidores')
      } else if (isFocoEstoque) {
        const listEst = Array.isArray(summary?.estoquePorDistribuidor)
          ? summary.estoquePorDistribuidor
          : []
        const totalRegEst = listEst.reduce(
          (acc: number, r: any) => acc + (Number(r.Registros) || 0),
          0
        )
        const wsEstoque = buildCorporateSheet({
          reportTitle: 'Posição de Estoque por Distribuidor',
          subtitle: 'RELATÓRIO EXCLUSIVO: POSIÇÃO ATUAL DE ESTOQUE POR UNIDADE DISTRIBUIDORA',
          periodLabel: 'Posição Consolidada Atual',
          distributorLabel: 'Distribuidores Ativos',
          headers: [
            { label: 'Distribuidor', key: 'label', align: 'left' },
            { label: 'Registros / SKUs em Estoque', key: 'Registros', align: 'right' },
          ],
          dataRows: listEst,
          totalsRow: ['TOTAL EM ESTOQUE', totalRegEst],
        })
        XLSX.utils.book_append_sheet(wb, wsEstoque, 'Posicao_Estoque')
      } else if (isFocoProdutosInvalidos) {
        const wsInvOnly = buildCorporateSheet({
          reportTitle: 'Auditoria de Produtos Inválidos',
          subtitle: 'RELATÓRIO EXCLUSIVO: PRODUTOS INVÁLIDOS PENDENTES DE HOMOLOGAÇÃO',
          periodLabel: 'Base Atual',
          distributorLabel: 'Todos os Distribuidores',
          headers: [
            { label: 'Código Importado / SKU', key: 'codigo', align: 'center' },
            { label: 'Descrição do Produto', key: 'descricao', align: 'left' },
            { label: 'Distribuidor Origem', key: 'distribuidor', align: 'left' },
          ],
          dataRows: summary?.produtosInvalidosRecentes || [],
        })
        XLSX.utils.book_append_sheet(wb, wsInvOnly, 'Produtos_Invalidos')
      } else if (isFocoSellout) {
        // Se veio de uma consulta específica no chat (ex: cliente/marca/distribuidor com reportData/chartData), exporta apenas aquela série
        if (
          activeChartRef &&
          Array.isArray(activeChartRef.data) &&
          activeChartRef.data.length > 0 &&
          (isReferencingLastChat || lowerEscopo.includes('deste sellout'))
        ) {
          const refChart = activeChartRef
          const wsSelloutChat = buildCorporateSheet({
            reportTitle: refChart.title,
            subtitle: `RELATÓRIO EXCLUSIVO DE SELLOUT — ${refChart.title.toUpperCase()}`,
            periodLabel: refChart.subtitle || 'Período Consultado',
            distributorLabel: 'Consolidado GID',
            headers: [
              { label: 'Competência / Indicador', key: 'label', align: 'left' },
              { label: 'Faturamento Sellout (R$)', key: 'Faturamento', align: 'right' },
              ...(refChart.data[0]?.Volume !== undefined
                ? [{ label: 'Volume Movimentado (Qtd)', key: 'Volume', align: 'right' as const }]
                : []),
              ...(refChart.data[0]?.PDVs_Ativos_Mes !== undefined
                ? [{ label: 'PDVs Positivados', key: 'PDVs_Ativos_Mes', align: 'right' as const }]
                : []),
              ...(refChart.data[0]?.SKUs_Vendidos !== undefined
                ? [{ label: 'SKUs Vendidos', key: 'SKUs_Vendidos', align: 'right' as const }]
                : []),
            ],
            dataRows: refChart.data,
            totalsRow: [
              'TOTAL ACUMULADO',
              refChart.data.reduce((acc, r) => acc + (Number(r.Faturamento) || 0), 0),
              ...(refChart.data[0]?.Volume !== undefined
                ? [refChart.data.reduce((acc, r) => acc + (Number(r.Volume) || 0), 0)]
                : []),
              ...(refChart.data[0]?.PDVs_Ativos_Mes !== undefined
                ? [refChart.data.reduce((acc, r) => acc + (Number(r.PDVs_Ativos_Mes) || 0), 0)]
                : []),
              ...(refChart.data[0]?.SKUs_Vendidos !== undefined
                ? [refChart.data.reduce((acc, r) => acc + (Number(r.SKUs_Vendidos) || 0), 0)]
                : []),
            ],
          })
          XLSX.utils.book_append_sheet(wb, wsSelloutChat, 'Sellout_Consultado')
        } else if (
          (lowerEscopo.includes('evoluc') || lowerEscopo.includes('mensal')) &&
          Array.isArray(summary?.selloutMensal) &&
          summary.selloutMensal.length > 0
        ) {
          const listMes = summary.selloutMensal
          const totalFatMes = listMes.reduce(
            (acc: number, r: any) => acc + (Number(r.Faturamento) || 0),
            0
          )
          const totalVolMes = listMes.reduce(
            (acc: number, r: any) => acc + (Number(r.Volume) || 0),
            0
          )
          const wsMensalOnly = buildCorporateSheet({
            reportTitle: 'Evolução Mensal de Sellout',
            subtitle: 'RELATÓRIO EXCLUSIVO: SÉRIE HISTÓRICA MENSAL DE SELLOUT (R$ & VOLUME)',
            periodLabel: 'Últimos Meses Consolidados',
            distributorLabel: 'Todos os Distribuidores',
            headers: [
              { label: 'Competência (Mês/Ano)', key: 'label', align: 'center' },
              { label: 'Faturamento Sellout (R$)', key: 'Faturamento', align: 'right' },
              { label: 'Volume Movimentado (Qtd)', key: 'Volume', align: 'right' },
            ],
            dataRows: listMes,
            totalsRow: ['TOTAL ACUMULADO', totalFatMes, totalVolMes],
          })
          XLSX.utils.book_append_sheet(wb, wsMensalOnly, 'Evolucao_Mensal')
        } else {
          const list = summary?.selloutPorDistribuidor || []
          const totalFat = list.reduce(
            (acc: number, r: any) => acc + (Number(r.Faturamento) || 0),
            0
          )
          const totalVol = list.reduce((acc: number, r: any) => acc + (Number(r.Volume) || 0), 0)
          const selloutRows = list.map((r: any, idx: number) => ({
            pos: `${idx + 1}º`,
            distribuidor: r.label,
            faturamento: Number(r.Faturamento) || 0,
            volume: Number(r.Volume) || 0,
            share: totalFat > 0 ? (Number(r.Faturamento) || 0) / totalFat : 0,
          }))
          const wsSelloutOnly = buildCorporateSheet({
            reportTitle: 'Ranking de Sellout por Distribuidor',
            subtitle: 'RELATÓRIO EXCLUSIVO: RANKING DE SELLOUT E FATURAMENTO POR DISTRIBUIDOR',
            periodLabel: 'Período Analisado',
            distributorLabel: 'Top Distribuidores GID',
            headers: [
              { label: 'Posição', key: 'pos', align: 'center' },
              { label: 'Distribuidor', key: 'distribuidor', align: 'left' },
              { label: 'Faturamento Sellout (R$)', key: 'faturamento', align: 'right' },
              { label: 'Volume (Qtd)', key: 'volume', align: 'right' },
              { label: 'Share Faturamento (%)', key: 'share', align: 'right' },
            ],
            dataRows: selloutRows,
            totalsRow: ['TOTAL', 'TOTAL TOP DISTRIBUIDORES', totalFat, totalVol, '100,0%'],
          })
          XLSX.utils.book_append_sheet(wb, wsSelloutOnly, 'Sellout_Distribuidores')
        }
      } else if (
        isReferencingLastChat &&
        activeChartRef &&
        Array.isArray(activeChartRef.data) &&
        activeChartRef.data.length > 0
      ) {
        const refChart = activeChartRef
        const wsChatCustom = buildCorporateSheet({
          reportTitle: refChart.title,
          subtitle: `DADOS EXPORTADOS DO CHAT GID IA — ${refChart.title.toUpperCase()}`,
          periodLabel: refChart.subtitle || 'Consulta em Tempo Real',
          distributorLabel: 'Consolidado GID',
          headers: [
            { label: 'Indicador / Período', key: 'label', align: 'left' },
            ...(refChart.series || []).map((s) => ({
              label: s.name,
              key: s.key,
              align: 'right' as const,
            })),
          ],
          dataRows: refChart.data,
          totalsRow: [
            'TOTAL CONSOLIDADO',
            ...(refChart.series || []).map((s) =>
              refChart.data.reduce((acc, row) => acc + (Number(row[s.key]) || 0), 0)
            ),
          ],
        })
        XLSX.utils.book_append_sheet(wb, wsChatCustom, 'Dados_Do_Chat')
      } else {
        // Relatório Corporativo Geral (Sellout & Faturamento — sem Regularidade)
        const kpiFormattedRows = [
          [
            cellText('Faturamento Sellout Acumulado (R$)', false, 'left', true),
            cellMoney(Number(kpis.selloutUltimos30DiasValor || 0), false, true),
            cellText('Financeiro / Sellout', false, 'center'),
          ],
          [
            cellText('Volume Movimentado (Unidades / Caixas)', true, 'left'),
            cellNumber(Number(kpis.selloutUltimos30DiasQtd || 0), true),
            cellText('Volume Comercial', true, 'center'),
          ],
          [
            cellText('Distribuidores Ativos Monitorados', false, 'left'),
            cellNumber(Number(kpis.totalDistribuidoresAtivos || 0), false),
            cellText('Malha de Distribuição', false, 'center'),
          ],
          [
            cellText('Registros de Estoque Consolidados', true, 'left'),
            cellNumber(Number(kpis.estoqueRegistrosRecentes || 0), true),
            cellText('Posição de Estoque', true, 'center'),
          ],
          [
            cellText('Carteira de Clientes / PDVs Cadastrados', false, 'left'),
            cellNumber(Number(kpis.totalClientesAtivos || 0), false),
            cellText('Cobertura de PDVs', false, 'center'),
          ],
          [
            cellText('Produtos Homologados no Catálogo', true, 'left'),
            cellNumber(Number(kpis.totalProdutosHomologados || 0), true),
            cellText('Catálogo de Produtos', true, 'center'),
          ],
        ]

        const wsResumo = buildCorporateSheet({
          reportTitle: 'Resumo Executivo de Indicadores (KPIs)',
          subtitle: 'RELATÓRIO EXECUTIVO GID — SELLOUT & FATURAMENTO (GID IA)',
          periodLabel: 'Consolidado Atual',
          distributorLabel: 'Todos os Distribuidores Ativos',
          headers: [
            { label: 'Indicador Executivo GID', align: 'left' },
            { label: 'Valor Consolidado', align: 'right' },
            { label: 'Módulo / Categoria', align: 'center' },
          ],
          dataRows: kpiFormattedRows,
        })
        XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo_KPIs')

        const listSell = summary?.selloutPorDistribuidor || []
        if (listSell.length > 0) {
          const totalFat = listSell.reduce(
            (acc: number, r: any) => acc + (Number(r.Faturamento) || 0),
            0
          )
          const totalVol = listSell.reduce(
            (acc: number, r: any) => acc + (Number(r.Volume) || 0),
            0
          )
          const selloutRows = listSell.map((r: any, idx: number) => ({
            pos: `${idx + 1}º`,
            distribuidor: r.label,
            faturamento: Number(r.Faturamento) || 0,
            volume: Number(r.Volume) || 0,
            share: totalFat > 0 ? (Number(r.Faturamento) || 0) / totalFat : 0,
          }))
          const wsSelloutExec = buildCorporateSheet({
            reportTitle: 'Ranking de Sellout por Distribuidor',
            subtitle: 'RELATÓRIO EXECUTIVO: RANKING DE SELLOUT E FATURAMENTO POR DISTRIBUIDOR',
            periodLabel: 'Período Analisado',
            distributorLabel: 'Top Distribuidores GID',
            headers: [
              { label: 'Posição', key: 'pos', align: 'center' },
              { label: 'Distribuidor', key: 'distribuidor', align: 'left' },
              { label: 'Faturamento Sellout (R$)', key: 'faturamento', align: 'right' },
              { label: 'Volume (Qtd)', key: 'volume', align: 'right' },
              { label: 'Share Faturamento (%)', key: 'share', align: 'right' },
            ],
            dataRows: selloutRows,
            totalsRow: ['TOTAL', 'TOTAL DISTRIBUIDORES', totalFat, totalVol, '100,0%'],
          })
          XLSX.utils.book_append_sheet(wb, wsSelloutExec, 'Sellout_Distribuidores')
        }

        if (wsLevantamentoBuilt) {
          XLSX.utils.book_append_sheet(wb, wsLevantamentoBuilt, 'Cadastro_Distribuidores')
        }
      }

      setTaskProgressPct(92)
      setTaskStepDetail('Etapa 4/4: Empacotando arquivo .xlsx com estilo contábil GID...')
      await waitDelay(160)

      const dateStr = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')
      const fileName = isFocoLevantamentoDist
        ? `Levantamento_Distribuidores_GID_${dateStr}.xlsx`
        : isFocoRegularidade
        ? `Relatorio_Regularidade_7Dias_GID_${dateStr}.xlsx`
        : isFocoSellout
        ? `Relatorio_Sellout_GID_${dateStr}.xlsx`
        : isFocoEstoque
        ? `Relatorio_Estoque_GID_${dateStr}.xlsx`
        : `Relatorio_Corporativo_GID_${dateStr}.xlsx`
      XLSX.writeFile(wb, fileName)

      setTaskProgressPct(100)
      setTaskStatusMsg('Planilha Excel (.xlsx) concluída!')
      setTaskStepDetail(`Download iniciado: ${fileName}`)
      toast.success('Planilha gerada com o Design Corporativo do GID!')

      const builtReportChartData: GisaChartPayload | undefined = isFocoRegularidade
        ? {
            type: 'bar',
            title: 'Regularidade de Envios — Dias Enviados nos Últimos 7 Dias',
            subtitle: `${kpis.distribuidoresComEnvioRecente || 0} distribuidores em dia de ${
              kpis.totalDistribuidoresAtivos || 0
            } ativos`,
            data: distribuidores7d.map((d: any) => ({
              Cod_Dist: String(d.codDist || d.cod_dist || d.id),
              label: d.nome,
              Regional: d.regional || 'Geral',
              DiasEnviados: Number(d.diasEnviados7d || 0),
              DiasPendentes: Number(d.diasPendentes7d || 0),
            })),
            series: [{ key: 'DiasEnviados', name: 'Dias Enviados (7d)', color: '#10b981' }],
          }
        : isFocoLevantamentoDist
        ? {
            type: 'bar',
            title: 'Levantamento Cadastral de Distribuidores Ativos',
            subtitle: `${distribuidores7d.length} distribuidores exportados com Cód. Dist, Nome, UF Principal e Telefone`,
            data: distribuidores7d.map((d: any) => ({
              Cod_Dist: String(d.codDist || d.cod_dist || d.id),
              label: d.nome,
              UF_Principal: d.ufPrincipal || d.regional || '-',
              Regional: d.regional || 'Geral',
              Telefone: d.telefone || 'Não informado',
              Responsavel: d.responsavel || '-',
              PDVs_UF_Principal: Number(d.totalPdvsUf || 0),
            })),
            series: [
              { key: 'PDVs_UF_Principal', name: 'PDVs na UF Principal', color: '#0284c7' },
            ],
          }
        : activeChartRef

      setMessages((prev) => [
        ...prev,
        {
          id: String(Date.now()),
          sender: 'gisa',
          text: isFocoLevantamentoDist
            ? `📊 **Planilha Excel do Levantamento de Distribuidores gerada com sucesso!**\n\n` +
              `• **Arquivo Baixado**: \`${fileName}\`\n` +
              `• **Aba Única Gerada**: \`Levantamento_Distribuidores\` (**${distribuidores7d.length}** distribuidores exportados com Cód. Dist, Nome, UF Principal e Telefone)\n\n` +
              `O download do arquivo Excel já foi concluído no seu navegador!`
            : isFocoRegularidade
            ? `📊 **Planilha Excel de Regularidade (Últimos 7 Dias) gerada com sucesso!**\n\n` +
              `• **Arquivo Baixado**: \`${fileName}\`\n` +
              `• **Aba Única Gerada**: \`Regularidade_Envios\` (**${
                kpis.totalDistribuidoresAtivos || distribuidores7d.length
              }** distribuidores auditados)\n\n` +
              `O download do arquivo Excel já foi concluído no seu navegador!`
            : isFocoSellout
            ? `📊 **Planilha Excel Exclusiva de Sellout gerada com sucesso!**\n\n` +
              `• **Arquivo Baixado**: \`${fileName}\`\n` +
              `• **Aba Única Gerada**: \`${wb.SheetNames[0] || 'Sellout'}\`\n\n` +
              `O download do arquivo Excel já foi concluído no seu navegador!`
            : isFocoEstoque
            ? `📊 **Planilha Excel Exclusiva de Estoque gerada com sucesso!**\n\n` +
              `• **Arquivo Baixado**: \`${fileName}\`\n` +
              `• **Aba Única Gerada**: \`Posicao_Estoque\`\n\n` +
              `O download do arquivo Excel já foi concluído no seu navegador!`
            : `📊 **Planilha Corporativa GID gerada com sucesso!**\n\n` +
              `• **Arquivo Baixado**: \`${fileName}\`\n` +
              `• **Abas Geradas**: \`${wb.SheetNames.join('`, `')}\`\n\n` +
              `O download do arquivo Excel já foi concluído no seu navegador!`,
          chartData: pediuGraficoNoChat ? builtReportChartData : undefined,
          reportData: builtReportChartData,
          actionButton: isFocoLevantamentoDist
            ? {
                label: 'Abrir Gestão de Unidades / Distribuidores',
                route: '/painel',
                pageId: 6,
              }
            : isFocoRegularidade
            ? {
                label: 'Abrir Calendário de Regularidade',
                route: '/painel',
                pageId: 1,
              }
            : isFocoEstoque
            ? {
                label: 'Abrir Análise de Estoque & Giro',
                route: '/painel',
                pageId: 2,
              }
            : {
                label: 'Abrir Validação de Sellout',
                route: '/painel',
                pageId: 11,
              },
          followUps: isFocoRegularidade
            ? [
                '📈 Quero ver as regularidades com gráficos',
                '📄 Gerar PDF apenas de Regularidade de Envios',
                '🔴 Listar apenas os distribuidores pendentes (> 7 dias)',
              ]
            : isFocoLevantamentoDist
            ? [
                '📈 Quero ver o Levantamento de Distribuidores com gráficos',
                '📄 Gerar PDF apenas do Levantamento de Distribuidores',
              ]
            : isFocoSellout
            ? [
                '📈 Quero ver a evolução de Sellout com gráficos',
                '📄 Gerar PDF apenas de Sellout',
              ]
            : isFocoEstoque
            ? [
                '📈 Quero ver a posição de Estoque com gráficos',
                '📄 Gerar PDF apenas de Estoque',
              ]
            : ['📄 Gerar PDF do Relatório Executivo'],
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ])
      await waitDelay(700)
    } catch (err: any) {
      if (progressTimer) {
        clearInterval(progressTimer)
        progressTimer = null
      }
      const msg =
        err?.response?.data?.error ||
        'Vincule e ative uma chave de IA para exportar os indicadores consolidados.'
      toast.error(msg)
      setMessages((prev) => [
        ...prev,
        {
          id: String(Date.now()),
          sender: 'gisa',
          text: `⚠️ **Não foi possível exportar o Excel agora:**\n\n${msg}`,
          actionButton: {
            label: 'Configurar Chave IA',
            route: '/painel',
            pageId: 14,
          },
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ])
    } finally {
      if (progressTimer) clearInterval(progressTimer)
      setIsExecutingTask(false)
      setTaskProgressPct(0)
      setTaskStatusMsg('')
      setTaskStepDetail('')
    }
  }

  // ==========================================
  // TAREFA 3: DISPARAR RELATÓRIO POR E-MAIL IMEDIATAMENTE
  // ==========================================
  const executeDispararEmail = async (options?: {
    period?: string
    emailTo?: string
    emailFormat?: 'PDF' | 'EXCEL' | 'CSV'
  }) => {
    setIsExecutingTask(true)
    setTaskProgressPct(18)
    setTaskStatusMsg('Disparando boletim executivo do GID por e-mail...')
    setTaskStepDetail('Etapa 1/3: Gerando anexo corporativo no servidor...')

    let progressTimer: ReturnType<typeof setInterval> | null = setInterval(() => {
      setTaskProgressPct((prev) => {
        if (prev < 50) {
          setTaskStepDetail('Etapa 2/3: Autenticando no servidor SMTP seguro (AES-256)...')
          return prev + 10
        }
        if (prev < 82) {
          setTaskStepDetail('Etapa 3/3: Transmitindo boletim e anexo para os destinatários...')
          return prev + 8
        }
        return prev
      })
    }, 380)

    try {
      const res = await api.post('/integracoes/relatorio-diario/testar', {
        recipientEmail: options?.emailTo || undefined,
        exportFormats: [options?.emailFormat || 'PDF'],
        periodWindow: options?.period || 'anual',
      })
      if (progressTimer) {
        clearInterval(progressTimer)
        progressTimer = null
      }
      setTaskProgressPct(100)
      setTaskStatusMsg('Boletim Executivo enviado por e-mail!')
      setTaskStepDetail(res.data?.message || 'Disparo concluído pelo servidor SMTP.')
      toast.success(res.data?.message || 'Relatório enviado por e-mail!')
      setMessages((prev) => [
        ...prev,
        {
          id: String(Date.now()),
          sender: 'gisa',
          text:
            `📧 **Relatório Executivo enviado por e-mail com sucesso!**\n\n` +
            `• **Formato anexado**: ${options?.emailFormat || 'PDF'}\n` +
            `• **Status**: ${res.data?.message || 'Disparo concluído pelo servidor SMTP.'}`,
          actionButton: {
            label: 'Gerenciar Agendamentos de E-mail',
            route: '/painel',
            pageId: 14,
          },
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ])
      await waitDelay(700)
    } catch (err: any) {
      if (progressTimer) {
        clearInterval(progressTimer)
        progressTimer = null
      }
      const msg =
        err?.response?.data?.error ||
        'Configure um agendamento de e-mail e o servidor SMTP na Central de Integrações.'
      toast.error(msg)
      setMessages((prev) => [
        ...prev,
        {
          id: String(Date.now()),
          sender: 'gisa',
          text: `⚠️ **Não foi possível disparar o e-mail agora:**\n\n${msg}`,
          actionButton: {
            label: 'Abrir Relatórios por E-mail',
            route: '/painel',
            pageId: 14,
          },
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ])
    } finally {
      if (progressTimer) clearInterval(progressTimer)
      setIsExecutingTask(false)
      setTaskProgressPct(0)
      setTaskStatusMsg('')
      setTaskStepDetail('')
    }
  }

  // ==========================================
  // UPLOAD DE PLANILHA (.XLSX / .CSV) PARA ANÁLISE NO CHAT
  // ==========================================
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''

    const userMsg: ChatMessage = {
      id: String(Date.now()),
      sender: 'user',
      text: `📎 Anexou planilha para análise: **${file.name}**`,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    }
    setMessages((prev) => [...prev, userMsg])

    try {
      const buffer = await file.arrayBuffer()
      const wb = XLSX.read(buffer, { type: 'array' })
      const sheetName = wb.SheetNames[0]
      const sheet = wb.Sheets[sheetName]
      const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' })

      const totalRows = rows.length
      const columns = totalRows > 0 ? Object.keys(rows[0]).slice(0, 8) : []
      const sampleText = JSON.stringify(rows.slice(0, 5))

      await handleSendMessage(
        `Analise os dados desta planilha anexada chamada "${file.name}" (Aba: ${sheetName}, Total de linhas: ${totalRows}, Colunas: ${columns.join(
          ', '
        )}). Amostra das primeiras linhas: ${sampleText}`,
        undefined,
        undefined,
        true
      )
    } catch {
      toast.error('Não foi possível ler a planilha anexada.')
    }
  }

  // ==========================================
  // ENVIO DE MENSAGENS & DISPATCHER DE AÇÕES
  // ==========================================
  const handleSendMessage = async (
    textToSend?: string,
    directTask?: 'pdf' | 'excel' | 'email',
    taskOptions?: any,
    skipUserBubble = false
  ) => {
    const text = (textToSend || inputQuery).trim()
    if (!text || isExecutingTask) return

    if (!skipUserBubble) {
      const userMsg: ChatMessage = {
        id: String(Date.now()),
        sender: 'user',
        text,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      }
      setMessages((prev) => [...prev, userMsg])
      setInputQuery('')
      setShowSlashPalette(false)
    }

    // Execução direta se veio do modal de comando ou intenção clara
    const lower = text.toLowerCase()

    // Verifica se o usuário digitou um atalho pessoal (/comando)
    const matchedShortcut = personalShortcuts.find(
      (sc) => sc.command.toLowerCase() === lower
    )
    if (matchedShortcut) {
      if (matchedShortcut.defaultAction === 'pdf') {
        await executeGerarPdf()
        return
      }
      if (matchedShortcut.defaultAction === 'excel') {
        await executeGerarExcel()
        return
      }
      return handleSendMessage(matchedShortcut.promptTemplate, undefined, undefined, true)
    }

    const isUploadedSpreadsheetPrompt =
      skipUserBubble && lower.startsWith('analise os dados desta planilha anexada')

    if (
      directTask === 'pdf' ||
      lower === '/pdf' ||
      (!isUploadedSpreadsheetPrompt &&
        lower.includes('pdf') &&
        (lower.includes('gerar') ||
          lower.includes('gera') ||
          lower.includes('baixar') ||
          lower.includes('baixa') ||
          lower.includes('exportar') ||
          lower.includes('exporta') ||
          lower.includes('esportar') ||
          lower.includes('esporta') ||
          lower.includes('relatorio') ||
          lower.includes('relatório') ||
          lower.includes('levantamento') ||
          lower.includes('regularidade') ||
          lower.includes('sellout') ||
          lower.includes('quero') ||
          lower.includes('preciso') ||
          lower.includes('manda') ||
          lower.includes('envia')))
    ) {
      const lastGisaMsg = [...messages].reverse().find((m) => m.sender === 'gisa')
      await executeGerarPdf(taskOptions?.reportScope || text, lastGisaMsg)
      return
    }

    if (
      directTask === 'excel' ||
      lower === '/excel' ||
      (!isUploadedSpreadsheetPrompt &&
        (lower.includes('excel') || lower.includes('planilha') || lower.includes('.xlsx')) &&
        (lower.includes('gerar') ||
          lower.includes('gera') ||
          lower.includes('baixar') ||
          lower.includes('baixa') ||
          lower.includes('exportar') ||
          lower.includes('exporta') ||
          lower.includes('esportar') ||
          lower.includes('esporta') ||
          lower.includes('relatorio') ||
          lower.includes('relatório') ||
          lower.includes('levantamento') ||
          lower.includes('distribuidor') ||
          lower.includes('informac') ||
          lower.includes('informaç') ||
          lower.includes('dados') ||
          lower.includes('quero') ||
          lower.includes('preciso') ||
          lower.includes('regularidade') ||
          lower.includes('sellout') ||
          lower.includes('estoque') ||
          lower.includes('manda') ||
          lower.includes('traz') ||
          lower.includes('passa') ||
          lower.includes('salva')))
    ) {
      const lastGisaMsg = [...messages].reverse().find((m) => m.sender === 'gisa')
      await executeGerarExcel(taskOptions?.reportScope || text, lastGisaMsg)
      return
    }

    if (directTask === 'email' || lower === '/email') {
      await executeDispararEmail(taskOptions)
      return
    }

    // Envia para o motor da GISA no backend (/v2/gid/integracoes/gisa/chat)
    setIsTyping(true)
    try {
      const historyPayload = messages
        .filter((m) => m.id !== 'welcome')
        .slice(-6)
        .map((m) => ({
          role: m.sender === 'user' ? ('user' as const) : ('assistant' as const),
          content: m.text,
        }))

      const res = await api.post('/integracoes/gisa/chat', {
        message: text,
        history: historyPayload,
      })

      const data = res.data || {}
      const contextualFallbackFollowUps =
        lower.includes('regularidade') || lower.includes('envio')
          ? [
              '📈 Quero ver as regularidades com gráficos',
              '📊 Exportar relatório Excel apenas de Regularidade (.xlsx)',
              '📄 Gerar PDF apenas de Regularidade de Envios',
            ]
          : lower.includes('levantamento') ||
            (lower.includes('distribuidor') &&
              (lower.includes('uf') || lower.includes('telefone')))
          ? [
              '📊 Exportar Levantamento de Distribuidores em Excel (.xlsx)',
              '📈 Quero ver o Levantamento de Distribuidores com gráficos',
              '📄 Gerar PDF apenas do Levantamento de Distribuidores',
            ]
          : lower.includes('estoque')
          ? [
              '📈 Quero ver a posição de Estoque com gráficos',
              '📊 Exportar planilha Excel apenas de Estoque (.xlsx)',
            ]
          : [
              '📈 Quero ver o Sellout com gráficos',
              '📊 Exportar planilha Excel apenas de Sellout (.xlsx)',
            ]

      const gisaMsg: ChatMessage = {
        id: String(Date.now() + 1),
        sender: 'gisa',
        text: data.reply || 'Análise concluída com sucesso.',
        chartData: data.chartData,
        reportData: data.reportData || data.chartData,
        actionButton: data.actionButton,
        followUps:
          Array.isArray(data.followUps) && data.followUps.length > 0
            ? data.followUps
            : contextualFallbackFollowUps,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      }

      setMessages((prev) => [...prev, gisaMsg])
    } catch (err: any) {
      const errMsg =
        err?.response?.data?.error ||
        'Para conversar com a GID IA em tempo real, cadastre e ative sua Chave de API de IA na aba "Chat & Chaves GID IA" da Central de Integrações.'
      setMessages((prev) => [
        ...prev,
        {
          id: String(Date.now() + 1),
          sender: 'gisa',
          text: `🔒 **Chave de IA Necessária:**\n\n${errMsg}`,
          actionButton: {
            label: 'Configurar Chave de IA Agora',
            route: '/painel',
            pageId: 14,
          },
          followUps: [
            '📄 Gerar PDF do Relatório',
            '📊 Baixar Planilha Excel',
          ],
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ])
    } finally {
      setIsTyping(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setInputQuery(val)
    if (val.startsWith('/')) {
      setShowSlashPalette(true)
    } else {
      setShowSlashPalette(false)
    }
  }

  const handleOpenConfigModal = (action: Exclude<GisaCommandActionType, null>) => {
    setSelectedCommandAction(action)
    setIsCommandModalOpen(true)
    setShowSlashPalette(false)
  }

  const handleSelectCommand = (cmd: SlashCommandOption) => {
    setInputQuery('')
    handleOpenConfigModal(cmd.action)
  }

  const handleNovaConversa = () => {
    const newId = `conv_${Date.now()}`
    setActiveConversationId(newId)
    localStorage.setItem('@GID:gisa_active_conv', newId)
    setMessages([])
    setShowHistoryDropdown(false)
  }

  const handleSelecionarConversa = (convId: string) => {
    const found = conversationsList.find((c) => c.id === convId)
    if (found) {
      setActiveConversationId(found.id)
      localStorage.setItem('@GID:gisa_active_conv', found.id)
      setMessages(found.messages)
    }
    setShowHistoryDropdown(false)
  }

  const clearChat = () => {
    setMessages([
      {
        id: String(Date.now()),
        sender: 'gisa',
        text: `Histórico limpo! Escolha uma das capacidades abaixo ou digite **/** para executar análises de **Sellout**, **Regularidade**, **Estoque**, **PDF** ou **Excel**.`,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      },
    ])
  }

  // Renderizador de Markdown leve e seguro
  const formatInline = (str: string) => {
    return str
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`([^`]+)`/g, '<code>$1</code>')
  }

  const renderFormattedText = (rawText: string) => {
    const lines = String(rawText || '').split('\n')
    return lines.map((line, idx) => {
      const trimmed = line.trim()
      if (trimmed.startsWith('•') || trimmed.startsWith('- ')) {
        const content = trimmed.replace(/^[•\-]\s*/, '')
        return <li key={idx} dangerouslySetInnerHTML={{ __html: formatInline(content) }} />
      }
      if (trimmed.startsWith('### ')) {
        const content = trimmed.replace(/^###\s*/, '')
        return (
          <p
            key={idx}
            style={{ fontWeight: 700, fontSize: '0.88rem', marginTop: 4 }}
            dangerouslySetInnerHTML={{ __html: formatInline(content) }}
          />
        )
      }
      if (!trimmed) {
        return <br key={idx} />
      }
      return <p key={idx} dangerouslySetInnerHTML={{ __html: formatInline(line) }} />
    })
  }

  const contextScreenTitle =
    location.pathname === '/integracoes'
      ? 'Integrações, API Keys & GID IA'
      : PAGE_NAMES[currentPageId] || 'Ecossistema GID'

  return (
    <>
      {/* 1. BOTÃO CIRCULAR FLUTUANTE ARRASTÁVEL NA LATERAL DIREITA */}
      {!isOpen && (
        <FloatingWidgetWrapper
          style={{ top: `${topPosition}px` }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          {showTooltip && (
            <FloatingTooltip>
              <BsStars size={14} color="#a6c10b" />
              Falar com a GID IA
            </FloatingTooltip>
          )}

          <DragIndicatorGrip title="Arraste para mover para cima ou para baixo">
            <GripDot />
            <GripDot />
            <GripDot />
          </DragIndicatorGrip>

          <FloatingBubbleButton
            type="button"
            onClick={handleBubbleClick}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            title="Assistente GID IA (Clique para abrir)"
          >
            <BubbleAvatarImg src={gisaAvatar} alt="GID IA" />
            <OnlineDotBadge title="GID IA Online" />
          </FloatingBubbleButton>
        </FloatingWidgetWrapper>
      )}

      {/* 2. DRAWER LATERAL DESLIZANTE DA GID IA */}
      {isOpen && (
        <>
          <DrawerBackdrop onClick={() => setIsOpen(false)} />

          <DrawerContainer $isExpanded={isExpanded}>
            {/* CABEÇALHO EXECUTIVO */}
            <DrawerHeader>
              <HeaderProfile>
                <HeaderAvatarWrapper>
                  <HeaderAvatarImg src={gisaAvatar} alt="GID IA" />
                </HeaderAvatarWrapper>
                <HeaderTitleGroup>
                  <HeaderNameRow>
                    <AssistantName>GID IA</AssistantName>
                    <StatusPill $locked={!aiStatus.gisaUnlocked}>
                      {aiStatus.gisaUnlocked ? 'Online' : 'Vincular IA'}
                    </StatusPill>
                  </HeaderNameRow>
                  <AssistantSubtitle>
                    Inteligência Analítica & Copilot GID
                  </AssistantSubtitle>
                </HeaderTitleGroup>
              </HeaderProfile>

              <HeaderActions>
                <HeaderIconButton
                  type="button"
                  onClick={handleNovaConversa}
                  title="Iniciar nova conversa (+)"
                >
                  <FiPlusCircle size={15} />
                </HeaderIconButton>
                <HeaderIconButton
                  type="button"
                  $active={showHistoryDropdown}
                  onClick={() => setShowHistoryDropdown((prev) => !prev)}
                  title="Histórico de conversas salvas"
                >
                  <FiClock size={15} />
                </HeaderIconButton>
                <HeaderIconButton
                  type="button"
                  onClick={() => setIsExpanded((prev) => !prev)}
                  title={isExpanded ? 'Reduzir largura do chat' : 'Expandir largura do chat'}
                >
                  {isExpanded ? <FiMinimize2 size={15} /> : <FiMaximize2 size={15} />}
                </HeaderIconButton>
                <HeaderIconButton
                  type="button"
                  onClick={clearChat}
                  title="Limpar conversa"
                >
                  <FiTrash2 size={15} />
                </HeaderIconButton>
                <HeaderIconButton
                  type="button"
                  onClick={() => setIsOpen(false)}
                  title="Fechar painel"
                >
                  <FiX size={16} />
                </HeaderIconButton>
              </HeaderActions>
            </DrawerHeader>

            {/* BARRA DE CONTEXTO E MODELO DE IA ATIVO */}
            <ContextBanner>
              <ContextBadge>
                <FiCpu size={14} />
                <span>{contextScreenTitle}</span>
              </ContextBadge>
              {aiStatus.gisaUnlocked ? (
                <ContextModelTag title={`Provedor: ${aiStatus.provider} | Modelo: ${aiStatus.model}`}>
                  {aiStatus.model}
                </ContextModelTag>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    handleActionNavigate('/painel', 14, 'Configurar Chave IA')
                  }}
                  style={{
                    background: 'rgba(245, 158, 11, 0.15)',
                    border: '1px solid rgba(245, 158, 11, 0.4)',
                    color: '#f59e0b',
                    borderRadius: 999,
                    padding: '2px 8px',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <FiKey size={11} /> Configurar IA
                </button>
              )}
            </ContextBanner>

            {/* BARRA DE AÇÕES RÁPIDAS 1-CLICK */}
            <TasksActionBar>
              <TaskActionBtn
                type="button"
                $variant="pdf"
                disabled={isExecutingTask}
                onClick={() => {
                  const lastUserMsg = [...messages].reverse().find((m) => m.sender === 'user')
                  const lastGisaMsg = [...messages]
                    .reverse()
                    .find((m) => m.sender === 'gisa' && m.id !== 'welcome')
                  executeGerarPdf(lastUserMsg?.text || undefined, lastGisaMsg)
                }}
                title="Gerar PDF focado no assunto atual do chat (ou Executivo Geral)"
              >
                <FiFileText size={13} /> PDF Executivo
              </TaskActionBtn>
              <TaskActionBtn
                type="button"
                $variant="excel"
                disabled={isExecutingTask}
                onClick={() => {
                  const lastUserMsg = [...messages].reverse().find((m) => m.sender === 'user')
                  const lastGisaMsg = [...messages]
                    .reverse()
                    .find((m) => m.sender === 'gisa' && m.id !== 'welcome')
                  executeGerarExcel(lastUserMsg?.text || undefined, lastGisaMsg)
                }}
                title="Exportar planilha Excel (.xlsx) focada no assunto atual do chat"
              >
                <FiGrid size={13} /> Planilha Excel
              </TaskActionBtn>
              <TaskActionBtn
                type="button"
                $variant="sellout"
                disabled={isExecutingTask}
                onClick={() => handleOpenConfigModal('sellout')}
                title="Analisar Sellout & Faturamento"
              >
                <FiTrendingUp size={13} /> Sellout R$
              </TaskActionBtn>
              <TaskActionBtn
                type="button"
                $variant="audit"
                disabled={isExecutingTask}
                onClick={() => handleOpenConfigModal('regularidade')}
                title="Auditar Regularidade de Envios"
              >
                <FiCheckCircle size={13} /> Auditar Envios
              </TaskActionBtn>
              <TaskActionBtn
                type="button"
                $variant="sellout"
                disabled={isExecutingTask}
                onClick={() => handleActionNavigate('/painel', 14, 'Treinar GID IA')}
                title="Abrir Treinamento Compartilhado da GID IA e Meus Atalhos Pessoais"
              >
                <BsStars size={13} /> Treinar / Atalhos
              </TaskActionBtn>
              {personalShortcuts
                .filter((sc) => sc.pinToBar)
                .map((sc) => (
                  <TaskActionBtn
                    key={sc.id}
                    type="button"
                    $variant="sellout"
                    disabled={isExecutingTask}
                    onClick={() => {
                      if (sc.defaultAction === 'pdf') {
                        executeGerarPdf()
                      } else if (sc.defaultAction === 'excel') {
                        executeGerarExcel()
                      } else {
                        handleSendMessage(sc.promptTemplate)
                      }
                    }}
                    title={`Seu Atalho Pessoal (${sc.command}): ${sc.promptTemplate}`}
                  >
                    <BsStars size={12} /> {sc.label}
                  </TaskActionBtn>
                ))}
            </TasksActionBar>

            {/* DROPDOWN DE HISTÓRICO DE CONVERSAS */}
            {showHistoryDropdown && (
              <div
                style={{
                  background: '#0f172a',
                  borderBottom: '1px solid #334155',
                  padding: '12px 16px',
                  maxHeight: '210px',
                  overflowY: 'auto',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#94a3b8',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Conversas Salvas
                  </span>
                  <button
                    type="button"
                    onClick={handleNovaConversa}
                    style={{
                      background: '#2563eb',
                      border: 'none',
                      color: '#fff',
                      fontSize: 11,
                      padding: '2px 8px',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                  >
                    + Nova
                  </button>
                </div>
                {conversationsList.length === 0 ? (
                  <div
                    style={{
                      fontSize: 12,
                      color: '#64748b',
                      textAlign: 'center',
                      padding: '10px 0',
                    }}
                  >
                    Nenhuma conversa anterior
                  </div>
                ) : (
                  conversationsList.map((c) => (
                    <div
                      key={c.id}
                      onClick={() => handleSelecionarConversa(c.id)}
                      style={{
                        padding: '8px 10px',
                        borderRadius: 6,
                        background: c.id === activeConversationId ? '#1e293b' : 'transparent',
                        border:
                          c.id === activeConversationId
                            ? '1px solid #38bdf8'
                            : '1px solid transparent',
                        color: c.id === activeConversationId ? '#38bdf8' : '#cbd5e1',
                        fontSize: 12,
                        cursor: 'pointer',
                        marginBottom: 4,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '230px',
                        }}
                      >
                        {c.title || 'Conversa GID'}
                      </span>
                      <span style={{ fontSize: 10, color: '#64748b' }}>
                        {new Date(c.createdAt).toLocaleDateString([], {
                          day: '2-digit',
                          month: '2-digit',
                        })}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* CARD DE PROCESSO EM EXECUÇÃO COM BARRA DE PROGRESSO */}
            {isExecutingTask && (
              <div style={{ padding: '0 18px', marginTop: '10px' }}>
                <TaskProgressCard $done={taskProgressPct >= 100}>
                  <TaskProgressHeader>
                    <TaskProgressTitleRow>
                      {taskProgressPct >= 100 ? (
                        <FiCheckCircle size={16} color="#10b981" style={{ flexShrink: 0 }} />
                      ) : (
                        <FiLoader size={16} color="#2563eb" className="spin-icon" />
                      )}
                      <span>{taskStatusMsg || 'Gerando arquivo pela GID IA...'}</span>
                    </TaskProgressTitleRow>
                    <TaskProgressPctBadge $done={taskProgressPct >= 100}>
                      {Math.round(taskProgressPct)}%
                    </TaskProgressPctBadge>
                  </TaskProgressHeader>

                  <TaskProgressBarTrack>
                    <TaskProgressBarFill
                      $progress={taskProgressPct}
                      $done={taskProgressPct >= 100}
                    />
                  </TaskProgressBarTrack>

                  {taskStepDetail && (
                    <TaskProgressStepDetail>
                      <span>{taskStepDetail}</span>
                      <span>
                        {taskProgressPct >= 100 ? 'Concluído' : 'Por favor, aguarde...'}
                      </span>
                    </TaskProgressStepDetail>
                  )}
                </TaskProgressCard>
              </div>
            )}

            {/* ÁREA DE MENSAGENS & DASHBOARD DE CAPACIDADES */}
            <ChatMessagesScroll>
              {messages.length <= 1 && (
                <WelcomeDashboard>
                  <WelcomeHero>
                    <h3>
                      <BsStars size={17} color="#a6c10b" />
                      GID IA — Inteligência Analítica GID
                    </h3>
                    <p>
                      Conectada aos indicadores em tempo real. Digite <strong>/</strong> no chat ou
                      clique em um atalho abaixo para configurar sua análise:
                    </p>
                  </WelcomeHero>

                  <CapabilitiesGrid>
                    <CapabilityCard
                      type="button"
                      $color="#0284c7"
                      onClick={() => handleOpenConfigModal('sellout')}
                    >
                      <CapabilityIconBox $color="#0284c7">
                        <FiTrendingUp size={17} />
                      </CapabilityIconBox>
                      <CapabilityContent>
                        <span className="cap-title">Sellout & Faturamento</span>
                        <span className="cap-desc">
                          Ranking por distribuidor e evolução mensal em R$ e volume.
                        </span>
                      </CapabilityContent>
                    </CapabilityCard>

                    <CapabilityCard
                      type="button"
                      $color="#8b5cf6"
                      onClick={() => handleOpenConfigModal('regularidade')}
                    >
                      <CapabilityIconBox $color="#8b5cf6">
                        <FiCheckCircle size={17} />
                      </CapabilityIconBox>
                      <CapabilityContent>
                        <span className="cap-title">Regularidade de Envios</span>
                        <span className="cap-desc">
                          Auditar distribuidores em dia vs pendentes há mais de 7 dias.
                        </span>
                      </CapabilityContent>
                    </CapabilityCard>

                    <CapabilityCard
                      type="button"
                      $color="#f97316"
                      onClick={() => handleOpenConfigModal('estoque')}
                    >
                      <CapabilityIconBox $color="#f97316">
                        <FiPackage size={17} />
                      </CapabilityIconBox>
                      <CapabilityContent>
                        <span className="cap-title">Estoque & Giro</span>
                        <span className="cap-desc">
                          Posição de estoque por distribuidor e catálogo de produtos.
                        </span>
                      </CapabilityContent>
                    </CapabilityCard>

                    <CapabilityCard
                      type="button"
                      $color="#10b981"
                      onClick={() => handleOpenConfigModal('pdvs')}
                    >
                      <CapabilityIconBox $color="#10b981">
                        <FiMapPin size={17} />
                      </CapabilityIconBox>
                      <CapabilityContent>
                        <span className="cap-title">Cobertura & PDVs</span>
                        <span className="cap-desc">
                          Carteira de clientes ativos, giro de PDVs e potencial IBGE.
                        </span>
                      </CapabilityContent>
                    </CapabilityCard>

                    <CapabilityCard
                      type="button"
                      $color="#ef4444"
                      onClick={() => handleOpenConfigModal('pdf')}
                    >
                      <CapabilityIconBox $color="#ef4444">
                        <FiFileText size={17} />
                      </CapabilityIconBox>
                      <CapabilityContent>
                        <span className="cap-title">Relatório em PDF</span>
                        <span className="cap-desc">
                          Compilar indicadores executivos do GID em PDF imediato.
                        </span>
                      </CapabilityContent>
                    </CapabilityCard>

                    <CapabilityCard
                      type="button"
                      $color="#16a34a"
                      onClick={() => handleOpenConfigModal('excel')}
                    >
                      <CapabilityIconBox $color="#16a34a">
                        <FiGrid size={17} />
                      </CapabilityIconBox>
                      <CapabilityContent>
                        <span className="cap-title">Planilha Excel Multi-Abas</span>
                        <span className="cap-desc">
                          Exportar arquivo .xlsx com KPIs, Sellout, Envios e Produtos.
                        </span>
                      </CapabilityContent>
                    </CapabilityCard>
                  </CapabilitiesGrid>
                </WelcomeDashboard>
              )}

              {messages.map((msg, msgIdx) => {
                const correspondingUserPrompt =
                  msgIdx > 0 && messages[msgIdx - 1]?.sender === 'user'
                    ? messages[msgIdx - 1].text
                    : msg.text

                return (
                  <MessageBubbleRow key={msg.id} $isUser={msg.sender === 'user'}>
                    {msg.sender === 'gisa' && <MiniAvatarImg src={gisaAvatar} alt="GID IA" />}
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        maxWidth: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <MessageCard $isUser={msg.sender === 'user'}>
                        {renderFormattedText(msg.text)}

                        {msg.chartData && (
                          <InteractiveChartCard
                            id={msg.id}
                            chartData={msg.chartData}
                            onExportExcel={() =>
                              executeGerarExcel(correspondingUserPrompt || msg.text, msg)
                            }
                          />
                        )}

                        {msg.sender === 'gisa' && msg.id !== 'welcome' && (
                          <MessageActionsToolbar>
                            <MessageExportButton
                              type="button"
                              $variant="excel"
                              disabled={isExecutingTask}
                              onClick={() =>
                                executeGerarExcel(correspondingUserPrompt || msg.text, msg)
                              }
                              title="Exportar exatamente os dados desta mensagem para uma planilha Excel (.xlsx)"
                            >
                              <FiGrid size={13} />
                              <span>Exportar Excel (.xlsx)</span>
                            </MessageExportButton>

                            <MessageExportButton
                              type="button"
                              $variant="pdf"
                              disabled={isExecutingTask}
                              onClick={() =>
                                executeGerarPdf(correspondingUserPrompt || msg.text, msg)
                              }
                              title="Baixar o relatório desta mensagem em PDF"
                            >
                              <FiFileText size={13} />
                              <span>Baixar PDF</span>
                            </MessageExportButton>

                            {!msg.chartData && (
                              <MessageExportButton
                                type="button"
                                $variant="chart"
                                disabled={isExecutingTask}
                                onClick={() => {
                                  if (msg.reportData) {
                                    setMessages((prev) =>
                                      prev.map((m) =>
                                        m.id === msg.id ? { ...m, chartData: msg.reportData } : m
                                      )
                                    )
                                  } else {
                                    handleSendMessage(
                                      `Quero ver com gráficos: ${correspondingUserPrompt || msg.text}`
                                    )
                                  }
                                }}
                                title="Exibir o gráfico visual interativo desta análise"
                              >
                                <FiBarChart2 size={13} />
                                <span>Ver com Gráfico</span>
                              </MessageExportButton>
                            )}

                            {msg.actionButton && (
                              <ActionButtonInChat
                                type="button"
                                onClick={() =>
                                  handleActionNavigate(
                                    msg.actionButton!.route,
                                    msg.actionButton!.pageId,
                                    msg.actionButton!.label
                                  )
                                }
                              >
                                {msg.actionButton.label}
                                <FiExternalLink size={13} />
                              </ActionButtonInChat>
                            )}
                          </MessageActionsToolbar>
                        )}
                      </MessageCard>

                      {msg.id !== 'welcome' &&
                        msgIdx === messages.length - 1 &&
                        msg.followUps &&
                        msg.followUps.length > 0 && (
                          <FollowUpsWrapper>
                            {msg.followUps.map((fUp, i) => (
                              <FollowUpChip
                                key={i}
                                type="button"
                                onClick={() => handleSendMessage(fUp)}
                              >
                                <span>{fUp}</span>
                                <BsStars size={11} />
                              </FollowUpChip>
                            ))}
                          </FollowUpsWrapper>
                        )}
                    </div>
                  </MessageBubbleRow>
                )
              })}

              {isTyping && (
                <MessageBubbleRow>
                  <MiniAvatarImg src={gisaAvatar} alt="GID IA" />
                  <TypingIndicator>
                    <TypingDot $delay="-0.32s" />
                    <TypingDot $delay="-0.16s" />
                    <TypingDot $delay="0s" />
                  </TypingIndicator>
                </MessageBubbleRow>
              )}

              <div ref={messagesEndRef} />
            </ChatMessagesScroll>

            {/* RODAPÉ COM SLASH COMMANDS [/], ANEXO DE PLANILHA E INPUT */}
            <DrawerFooter>
              {showSlashPalette && (
                <SlashPaletteMenu>
                  {personalShortcuts.length > 0 && (
                    <>
                      <div
                        style={{
                          padding: '4px 8px',
                          fontSize: '10px',
                          fontWeight: 700,
                          color: '#38bdf8',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        ⚡ Meus Atalhos Pessoais ({user?.name || user?.nickname})
                      </div>
                      {personalShortcuts.map((sc) => (
                        <SlashPaletteItem
                          key={`sc-${sc.id}`}
                          type="button"
                          onClick={() => {
                            setInputQuery('')
                            setShowSlashPalette(false)
                            if (sc.defaultAction === 'pdf') {
                              executeGerarPdf()
                            } else if (sc.defaultAction === 'excel') {
                              executeGerarExcel()
                            } else {
                              handleSendMessage(sc.promptTemplate)
                            }
                          }}
                        >
                          <SlashPaletteItemIcon $color="#0284c7">
                            <BsStars size={14} />
                          </SlashPaletteItemIcon>
                          <SlashPaletteItemInfo>
                            <SlashPaletteItemCommand>
                              {sc.command}
                              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>
                                • {sc.label}
                              </span>
                            </SlashPaletteItemCommand>
                            <SlashPaletteItemDesc>{sc.promptTemplate}</SlashPaletteItemDesc>
                          </SlashPaletteItemInfo>
                        </SlashPaletteItem>
                      ))}
                    </>
                  )}
                  <div
                    style={{
                      padding: '4px 8px',
                      fontSize: '10px',
                      fontWeight: 700,
                      color: '#94a3b8',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Comandos Rápidos da GID IA (/)
                  </div>
                  {SLASH_COMMANDS_LIST.map((cmd) => (
                    <SlashPaletteItem
                      key={cmd.command}
                      type="button"
                      onClick={() => handleSelectCommand(cmd)}
                    >
                      <SlashPaletteItemIcon $color={cmd.color}>{cmd.icon}</SlashPaletteItemIcon>
                      <SlashPaletteItemInfo>
                        <SlashPaletteItemCommand>
                          {cmd.command}
                          <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>
                            • {cmd.label}
                          </span>
                        </SlashPaletteItemCommand>
                        <SlashPaletteItemDesc>{cmd.desc}</SlashPaletteItemDesc>
                      </SlashPaletteItemInfo>
                    </SlashPaletteItem>
                  ))}
                </SlashPaletteMenu>
              )}

              <input
                type="file"
                ref={fileInputRef}
                accept=".xlsx, .xls, .csv"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />

              <ChatInputForm
                onSubmit={(e) => {
                  e.preventDefault()
                  handleSendMessage()
                }}
              >
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  title="Anexar planilha (.xlsx, .csv) para a GID IA analisar"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#64748b',
                    cursor: 'pointer',
                    padding: '0.35rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '0.375rem',
                  }}
                >
                  <FiPaperclip size={17} />
                </button>

                <SlashCommandButton
                  type="button"
                  $active={showSlashPalette}
                  onClick={() => setShowSlashPalette((prev) => !prev)}
                  title="Ver comandos rápidos (/)"
                >
                  [/]
                </SlashCommandButton>

                <ChatInputField
                  type="text"
                  value={inputQuery}
                  onChange={handleInputChange}
                  placeholder="Pergunte à GID IA ou digite / para comandos..."
                  autoFocus
                />

                <SendMessageButton
                  type="submit"
                  $hasText={Boolean(inputQuery.trim())}
                  disabled={!inputQuery.trim() || isTyping || isExecutingTask}
                  title="Enviar mensagem"
                >
                  <FiSend size={15} />
                </SendMessageButton>
              </ChatInputForm>
            </DrawerFooter>
          </DrawerContainer>
        </>
      )}

      {/* 3. MODAL DE CONFIGURAÇÃO DE COMANDOS SLASH (/) */}
      <CommandConfigModal
        isOpen={isCommandModalOpen}
        onClose={() => setIsCommandModalOpen(false)}
        action={selectedCommandAction}
        onExecute={(query, directTask, taskOptions) =>
          handleSendMessage(query, directTask, taskOptions)
        }
      />
    </>
  )
}

export default GisaAssistant
